import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Send, Mail, MessageSquare, Search, Phone, Video, Paperclip, Smile, Plus, X, Trash2, Link as LinkIcon, Edit, RefreshCw, Clock, ArrowLeft, Calendar, Users, Check, AlertCircle, Smartphone, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { contactsAPI, messagesAPI, conversationsAPI, outlookAPI, scheduledMessagesAPI, outlookEmailAPI, outlookCalendarAuthAPI, personalPhoneAPI, apiCall, chatAPI, scheduleAPI, pushcutAPI, macMessagesAPI, macMessagesBridgeAPI, callsAPI } from '../services/api';
import { useLocation } from 'react-router-dom';

const MessageCenter = () => {
  const location = useLocation();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // all, texts, emails, personal
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Helper function to format timestamps consistently
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      // Try to parse as ISO date first
      const date = new Date(timestamp);
      
      // Check if valid date
      if (isNaN(date.getTime())) {
        // If not a valid date, return as-is (might be already formatted string like "02:09 AM")
        return timestamp;
      }
      
      // Format as: "Nov 13, 2025 at 12:53 AM"
      const month = date.toLocaleString('en-US', { month: 'short' });
      const day = date.getDate();
      const year = date.getFullYear();
      const time = date.toLocaleString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      });
      
      return `${month} ${day}, ${year} at ${time}`;
    } catch (error) {
      // If any error, return original timestamp
      return timestamp;
    }
  };
  
  // Helper function to convert 24-hour time to 12-hour format
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    let hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${minutes} ${ampm}`;
  };
  
  // Outlook integration state
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [outlookToken, setOutlookToken] = useState(localStorage.getItem('outlook_token'));
  const [emailFolders, setEmailFolders] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  
  // Outlook Email integration state (using calendar auth)
  const [outlookEmailConnected, setOutlookEmailConnected] = useState(false);
  const [outlookEmails, setOutlookEmails] = useState([]);
  const [selectedEmailFolder, setSelectedEmailFolder] = useState('inbox');
  const [isSyncingEmails, setIsSyncingEmails] = useState(false);
  const [lastEmailSyncTime, setLastEmailSyncTime] = useState(null);
  const syncEmailIntervalRef = useRef(null);

  const [checkingOutlookConnection, setCheckingOutlookConnection] = useState(true);

  // Scheduled messages state
  const [scheduledMessages, setScheduledMessages] = useState([]);

  // Email reply state
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [isEditingGroupName, setIsEditingGroupName] = useState(false);
  const [editedGroupName, setEditedGroupName] = useState('');

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);


  
  // Compose message dialog state
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [icloudContacts, setIcloudContacts] = useState([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [useManualEntry, setUseManualEntry] = useState(false);
  const [selectedContact, setSelectedContact] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualName, setManualName] = useState('');
  const [messageType, setMessageType] = useState('sms');
  const [composeMessage, setComposeMessage] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [sending, setSending] = useState(false);
  
  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // Link dialog state
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  
  // Draft response state
  const [showDraft, setShowDraft] = useState(false);
  const [regeneratingDraft, setRegeneratingDraft] = useState(false);
  
  // Schedule message dialog state
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [messageToSchedule, setMessageToSchedule] = useState('');
  const [isDraftSchedule, setIsDraftSchedule] = useState(false);
  
  // Edit scheduled message state
  const [isEditScheduleOpen, setIsEditScheduleOpen] = useState(false);
  const [editingScheduledMsg, setEditingScheduledMsg] = useState(null);
  const [editScheduleText, setEditScheduleText] = useState('');
  const [editScheduleTime, setEditScheduleTime] = useState('');
  
  // Scheduled messages for current conversation
  const [conversationScheduledMsgs, setConversationScheduledMsgs] = useState([]);
  
  // Contact edit dialog state
  const [isEditContactOpen, setIsEditContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    category: '',
    notes: '',
    image: ''
  });

  // Add to existing meeting modal
  const [showMeetingSelector, setShowMeetingSelector] = useState(false);
  const [existingMeetings, setExistingMeetings] = useState([]);
  const [selectedMessageForMeeting, setSelectedMessageForMeeting] = useState(null);
  const [isExtractingMeeting, setIsExtractingMeeting] = useState(false);
  
  // Pushcut integration state
  const [pushcutSecret, setPushcutSecret] = useState(localStorage.getItem('pushcut_secret') || '');
  const [pushcutEnabled, setPushcutEnabled] = useState(localStorage.getItem('pushcut_enabled') === 'true');
  const [sendingViaPushcut, setSendingViaPushcut] = useState(false);
  const [pushcutResult, setPushcutResult] = useState(null);
  
  // Mac Messages integration state
  const [macMessagesEnabled, setMacMessagesEnabled] = useState(localStorage.getItem('mac_messages_enabled') === 'true');
  const [sendingViaMac, setSendingViaMac] = useState(false);

  // Mac Messages Bridge state (for cloud sync)
  const [macBridgeStatus, setMacBridgeStatus] = useState({ connected: false, last_sync: null, mac_name: null });
  const [macBridgeConversations, setMacBridgeConversations] = useState([]);
  const [macBridgeMessages, setMacBridgeMessages] = useState([]);
  const [loadingMacBridge, setLoadingMacBridge] = useState(false);

  // Conversations state - must be declared before useEffects that use it
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [initialRender, setInitialRender] = useState(false); // For instant page shell

  // Call state
  const [isCallingInProgress, setIsCallingInProgress] = useState(false);

  // Handle phone call from conversation
  const handleMakeCall = async (phoneNumber, contactName) => {
    if (!phoneNumber) {
      alert('No phone number available for this contact');
      return;
    }

    // Clean the phone number
    const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');
    
    if (isCallingInProgress) {
      alert('A call is already in progress');
      return;
    }

    const confirmCall = window.confirm(
      `📞 Call ${contactName || cleanPhone}?\n\nThis will initiate a call from your Twilio number to ${cleanPhone}`
    );

    if (!confirmCall) return;

    setIsCallingInProgress(true);
    
    try {
      const response = await callsAPI.makeCall(cleanPhone, contactName);
      
      if (response.status === 'success' || response.call_sid) {
        alert(`✅ Call initiated!\n\nCalling ${contactName || cleanPhone}...\n\nCall SID: ${response.call_sid}`);
      } else {
        throw new Error(response.message || 'Failed to initiate call');
      }
    } catch (error) {
      console.error('Failed to make call:', error);
      alert(`❌ Failed to make call\n\n${error.message || 'Unknown error'}\n\nPlease check your Twilio configuration.`);
    } finally {
      setIsCallingInProgress(false);
    }
  };

  // Normalize phone number for comparison (remove spaces, dashes, parentheses, +1)
  const normalizePhone = (phone) => {
    if (!phone) return '';
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Remove leading 1 if present (US country code)
    return digits.startsWith('1') && digits.length === 11 ? digits.substring(1) : digits;
  };

  // Create a phone number lookup map for O(1) lookups instead of O(n) - MUST BE BEFORE useEffect that uses it
  const phoneToNameMap = useMemo(() => {
    if (!icloudContacts.length) return new Map();
    
    const map = new Map();
    let mappedCount = 0;
    
    for (const contact of icloudContacts) {
      // Check phone_numbers array
      if (contact.phone_numbers && Array.isArray(contact.phone_numbers)) {
        for (const phone of contact.phone_numbers) {
          const phoneStr = phone.number || phone;
          const normalized = normalizePhone(phoneStr);
          if (normalized && !map.has(normalized)) {
            map.set(normalized, contact.name);
            mappedCount++;
          }
        }
      }
      
      // Also check direct phone field
      if (contact.phone) {
        const normalized = normalizePhone(contact.phone);
        if (normalized && !map.has(normalized)) {
          map.set(normalized, contact.name);
          mappedCount++;
        }
      }
    }
    
    console.log(`📞 Built phone lookup map with ${mappedCount} numbers from ${icloudContacts.length} contacts`);
    return map;
  }, [icloudContacts]);
  
  // Find contact name by phone number - now O(1) instead of O(n)! - MUST BE BEFORE useEffect that uses it
  const getContactNameByPhone = useCallback((phoneNumber) => {
    if (!phoneNumber || !phoneToNameMap.size) {
      return null;
    }
    
    const normalizedPhone = normalizePhone(phoneNumber);
    const name = phoneToNameMap.get(normalizedPhone);
    
    if (name) {
      console.log(`✅ Quick match: ${phoneNumber} → ${name}`);
    }
    
    return name || null;
  }, [phoneToNameMap]);

  // Fetch contacts on component mount
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const data = await contactsAPI.getAll();
        setContacts(data);
      } catch (error) {
        console.error('Error fetching contacts:', error);
      }
    };
    
    fetchContacts();
  }, []);

  // Fetch Mac Messages Bridge status and conversations
  useEffect(() => {
    const fetchMacBridgeData = async () => {
      try {
        // Get bridge status
        const status = await macMessagesBridgeAPI.getStatus();
        setMacBridgeStatus(status);
        
        if (status.connected) {
          // Get conversations if bridge is connected
          const convData = await macMessagesBridgeAPI.getConversations();
          const macConvs = (convData.conversations || []).map(conv => {
            // Use display_name from backend or participants
            let displayName = conv.display_name || conv.participants?.[0] || 'Unknown';
            const phoneNumber = conv.participants?.[0] || '';
            
            // Try to match phone number with saved contacts
            if (phoneNumber && phoneToNameMap.size > 0) {
              const contactName = getContactNameByPhone(phoneNumber);
              if (contactName) {
                displayName = contactName;
                console.log(`✅ Mac Bridge: Matched ${phoneNumber} → ${contactName}`);
              }
            }
            
            return {
              id: `mac_${conv.chat_id}`,
              chat_id: conv.chat_id,
              name: displayName,
              originalName: conv.display_name || conv.participants?.[0],
              type: 'mac_bridge',
              avatar: (displayName || '??').substring(0, 2).toUpperCase(),
              lastMessage: conv.last_message || '',
              timestamp: conv.last_message_time || '',
              unread: conv.unread_count || 0,
              participants: conv.participants || [],
              phone: phoneNumber,
              // Include category information from backend
              messageCategory: conv.category || 'general',
              category: conv.category || 'general',
              categories: conv.categories || [conv.category || 'general']
            };
          });
          setMacBridgeConversations(macConvs);
        }
      } catch (error) {
        console.error('Error fetching Mac Bridge data:', error);
      }
    };
    
    fetchMacBridgeData();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchMacBridgeData, 30000);
    return () => clearInterval(interval);
  }, [phoneToNameMap, getContactNameByPhone]);

  // Handle navigation from contacts page
  useEffect(() => {
    if (location.state?.contact) {
      const { contact, action } = location.state;
      
      // Check if there's an existing conversation with this contact
      const existingConversation = conversations.find(conv => 
        conv.contact_id === contact.id || 
        (conv.phone && contact.phone && conv.phone === contact.phone) ||
        (conv.email && contact.email && conv.email === contact.email)
      );
      
      if (existingConversation) {
        // Select the existing conversation instead of creating a new one
        console.log(`📱 Found existing conversation with ${contact.name}, opening it...`);
        setSelectedConversation(existingConversation);
        
        // Switch to the appropriate tab based on conversation type
        if (existingConversation.type === 'email') {
          setActiveTab('emails');
        } else if (existingConversation.type === 'sms') {
          setActiveTab('texts');
        }
      } else {
        // No existing conversation found, open compose dialog to create a new one
        console.log(`📱 No existing conversation with ${contact.name}, opening compose dialog...`);
        
        // Set message type
        setMessageType(action === 'email' ? 'email' : 'sms');
        
        // Pre-select the contact
        setSelectedContact(contact.id);
        
        // Open compose dialog
        setIsComposeOpen(true);
      }
      
      // Clear location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, conversations]);

  // Load iCloud contacts for phone number matching (with caching)
  const loadIcloudContacts = async () => {
    try {
      // Try to load from sessionStorage first (instant!)
      const cachedContacts = sessionStorage.getItem('message_center_contacts');
      const cacheTimestamp = sessionStorage.getItem('message_center_contacts_timestamp');
      
      if (cachedContacts && cacheTimestamp) {
        const cacheAge = Date.now() - parseInt(cacheTimestamp);
        const cacheValidDuration = 5 * 60 * 1000; // 5 minutes
        
        // Use cache if less than 5 minutes old
        if (cacheAge < cacheValidDuration) {
          const contacts = JSON.parse(cachedContacts);
          setIcloudContacts(contacts);
          setContactsLoaded(true);
          console.log(`⚡ INSTANT LOAD: ${contacts.length} contacts from cache (${Math.round(cacheAge / 1000 / 60)} min old)`);
          
          // Don't call fetchConversations here - let initializeData handle it
          return;
        }
      }
      
      // If no cache or expired, fetch from API
      console.log('📱 Fetching contacts from API...');
      const data = await apiCall('/icloud-contacts/list?user_id=dwoodson&skip=0&limit=5000');
      const contacts = data.contacts || [];
      
      setIcloudContacts(contacts);
      setContactsLoaded(true);
      console.log(`📱 Loaded ${contacts.length} iCloud contacts from API`);
      
      // Cache the contacts
      try {
        sessionStorage.setItem('message_center_contacts', JSON.stringify(contacts));
        sessionStorage.setItem('message_center_contacts_timestamp', Date.now().toString());
        console.log('✅ Contacts cached in sessionStorage');
      } catch (e) {
        console.log('⚠️ Failed to cache contacts:', e);
      }
      
      // Don't call fetchConversations here - let initializeData handle it
    } catch (error) {
      console.error('Error loading iCloud contacts:', error);
      setContactsLoaded(true);
    }
  };

  // MOCKUP DATA - Replace with real API calls when needed
  const getMockupConversations = () => {
    return [
      {
        id: 'mock-1',
        name: 'Sarah Johnson',
        type: 'personal_phone',
        folder: 'inbox',
        lastMessage: 'Sounds great! See you at the coffee shop at 3pm',
        timestamp: 'Dec 29, 2025 at 2:45 PM',
        unread: 2,
        avatar: 'SJ',
        phone: '+1 (555) 123-4567',
        messageCategory: 'personal',
        category: 'personal',
        categories: ['personal'],
        messages: [
          { id: 'm1', sender: 'Sarah Johnson', text: 'Hey! Are you free this afternoon?', time: '2:30 PM', sent: true, is_from_me: false },
          { id: 'm2', sender: 'You', text: 'Yes, I am! What did you have in mind?', time: '2:35 PM', sent: true, is_from_me: true },
          { id: 'm3', sender: 'Sarah Johnson', text: 'Coffee? I found this amazing new place downtown', time: '2:40 PM', sent: true, is_from_me: false },
          { id: 'm4', sender: 'You', text: 'Perfect! What time works for you?', time: '2:42 PM', sent: true, is_from_me: true },
          { id: 'm5', sender: 'Sarah Johnson', text: 'Sounds great! See you at the coffee shop at 3pm', time: '2:45 PM', sent: true, is_from_me: false }
        ]
      },
      {
        id: 'mock-2',
        name: 'Michael Chen',
        type: 'text',
        folder: 'inbox',
        lastMessage: 'Thanks for the update. Let me review and get back to you',
        timestamp: 'Dec 29, 2025 at 11:20 AM',
        unread: 0,
        avatar: 'MC',
        phone: '+1 (555) 234-5678',
        messageCategory: 'meeting_request',
        category: 'meeting_request',
        categories: ['meeting_request'],
        messages: [
          { id: 'm6', sender: 'Michael Chen', text: 'Hi, can we schedule a meeting to discuss the Q1 roadmap?', time: '10:15 AM', sent: true, is_from_me: false },
          { id: 'm7', sender: 'You', text: 'Absolutely! How about Tuesday at 2pm?', time: '10:30 AM', sent: true, is_from_me: true },
          { id: 'm8', sender: 'Michael Chen', text: 'Tuesday works perfectly. I will send a calendar invite', time: '10:45 AM', sent: true, is_from_me: false },
          { id: 'm9', sender: 'You', text: 'Great! I have prepared some notes for the discussion', time: '11:15 AM', sent: true, is_from_me: true },
          { id: 'm10', sender: 'Michael Chen', text: 'Thanks for the update. Let me review and get back to you', time: '11:20 AM', sent: true, is_from_me: false }
        ]
      },
      {
        id: 'mock-3',
        name: 'Emma Williams',
        type: 'mac_bridge',
        folder: 'inbox',
        lastMessage: 'I would love to collaborate! When can we connect?',
        timestamp: 'Dec 29, 2025 at 9:15 AM',
        unread: 1,
        avatar: 'EW',
        phone: '+1 (555) 345-6789',
        messageCategory: 'opportunity',
        category: 'opportunity',
        categories: ['opportunity'],
        participants: ['+1 (555) 345-6789'],
        messages: [
          { id: 'm11', sender: 'Emma Williams', text: 'Hi! I came across your work and I am really impressed', time: '9:00 AM', sent: true, is_from_me: false },
          { id: 'm12', sender: 'You', text: 'Thank you so much! That means a lot', time: '9:05 AM', sent: true, is_from_me: true },
          { id: 'm13', sender: 'Emma Williams', text: 'I am working on a project that could use your expertise', time: '9:10 AM', sent: true, is_from_me: false },
          { id: 'm14', sender: 'Emma Williams', text: 'I would love to collaborate! When can we connect?', time: '9:15 AM', sent: true, is_from_me: false }
        ]
      },
      {
        id: 'mock-4',
        name: 'David Martinez',
        type: 'personal_phone',
        folder: 'inbox',
        lastMessage: 'Just landed! Traffic is crazy, might be 15 min late',
        timestamp: 'Dec 28, 2025 at 6:30 PM',
        unread: 1,
        avatar: 'DM',
        phone: '+1 (555) 456-7890',
        messageCategory: 'travel',
        category: 'travel',
        categories: ['travel'],
        messages: [
          { id: 'm15', sender: 'David Martinez', text: 'Flight delayed by 2 hours. Will keep you posted', time: '4:00 PM', sent: true, is_from_me: false },
          { id: 'm16', sender: 'You', text: 'No worries! Safe travels', time: '4:05 PM', sent: true, is_from_me: true },
          { id: 'm17', sender: 'David Martinez', text: 'Finally boarding! Should arrive around 6pm', time: '5:45 PM', sent: true, is_from_me: false },
          { id: 'm18', sender: 'David Martinez', text: 'Just landed! Traffic is crazy, might be 15 min late', time: '6:30 PM', sent: true, is_from_me: false }
        ]
      },
      {
        id: 'mock-5',
        name: 'Jessica Taylor',
        type: 'text',
        folder: 'inbox',
        lastMessage: 'Perfect! I will bring the presentation deck',
        timestamp: 'Dec 28, 2025 at 3:45 PM',
        unread: 0,
        avatar: 'JT',
        phone: '+1 (555) 567-8901',
        messageCategory: 'introduction',
        category: 'introduction',
        categories: ['introduction'],
        messages: [
          { id: 'm19', sender: 'Jessica Taylor', text: 'Hi! Lisa mentioned you might be interested in our product', time: '2:00 PM', sent: true, is_from_me: false },
          { id: 'm20', sender: 'You', text: 'Yes! I have heard great things. Would love to learn more', time: '2:15 PM', sent: true, is_from_me: true },
          { id: 'm21', sender: 'Jessica Taylor', text: 'Fantastic! Can I schedule a demo for next week?', time: '2:30 PM', sent: true, is_from_me: false },
          { id: 'm22', sender: 'You', text: 'Wednesday at 10am works for me', time: '3:30 PM', sent: true, is_from_me: true },
          { id: 'm23', sender: 'Jessica Taylor', text: 'Perfect! I will bring the presentation deck', time: '3:45 PM', sent: true, is_from_me: false }
        ]
      },
      {
        id: 'mock-6',
        name: 'Robert Anderson',
        type: 'personal_phone',
        folder: 'inbox',
        lastMessage: 'URGENT: Server is down! Need your help ASAP',
        timestamp: 'Dec 28, 2025 at 1:20 PM',
        unread: 3,
        avatar: 'RA',
        phone: '+1 (555) 678-9012',
        messageCategory: 'urgent',
        category: 'urgent',
        categories: ['urgent'],
        messages: [
          { id: 'm24', sender: 'Robert Anderson', text: 'URGENT: Server is down! Need your help ASAP', time: '1:20 PM', sent: true, is_from_me: false }
        ]
      },
      {
        id: 'mock-7',
        name: 'Amanda Lee',
        type: 'text',
        folder: 'inbox',
        lastMessage: 'The contract looks good. Ready to sign whenever you are',
        timestamp: 'Dec 27, 2025 at 4:15 PM',
        unread: 0,
        avatar: 'AL',
        phone: '+1 (555) 789-0123',
        messageCategory: 'general',
        category: 'general',
        categories: ['general'],
        messages: [
          { id: 'm25', sender: 'Amanda Lee', text: 'I reviewed the contract terms. Just a few minor questions', time: '2:00 PM', sent: true, is_from_me: false },
          { id: 'm26', sender: 'You', text: 'Of course! Happy to clarify anything', time: '2:30 PM', sent: true, is_from_me: true },
          { id: 'm27', sender: 'Amanda Lee', text: 'Thanks for explaining. Everything makes sense now', time: '3:45 PM', sent: true, is_from_me: false },
          { id: 'm28', sender: 'Amanda Lee', text: 'The contract looks good. Ready to sign whenever you are', time: '4:15 PM', sent: true, is_from_me: false }
        ]
      },
      {
        id: 'mock-8',
        name: 'Kevin Brown',
        type: 'mac_bridge',
        folder: 'inbox',
        lastMessage: 'Awesome! Looking forward to it',
        timestamp: 'Dec 27, 2025 at 11:30 AM',
        unread: 0,
        avatar: 'KB',
        phone: '+1 (555) 890-1234',
        messageCategory: 'personal',
        category: 'personal',
        categories: ['personal'],
        participants: ['+1 (555) 890-1234'],
        messages: [
          { id: 'm29', sender: 'Kevin Brown', text: 'Hey! Want to grab lunch this week?', time: '10:00 AM', sent: true, is_from_me: false },
          { id: 'm30', sender: 'You', text: 'Sure! Thursday works for me', time: '10:45 AM', sent: true, is_from_me: true },
          { id: 'm31', sender: 'Kevin Brown', text: 'Thursday it is! I know a great place on 5th street', time: '11:15 AM', sent: true, is_from_me: false },
          { id: 'm32', sender: 'Kevin Brown', text: 'Awesome! Looking forward to it', time: '11:30 AM', sent: true, is_from_me: false }
        ]
      },
      {
        id: 'mock-9',
        name: 'Rachel Green',
        type: 'personal_phone',
        folder: 'inbox',
        lastMessage: 'Conference room booked for 2pm tomorrow',
        timestamp: 'Dec 26, 2025 at 5:00 PM',
        unread: 0,
        avatar: 'RG',
        phone: '+1 (555) 901-2345',
        messageCategory: 'meeting_request',
        category: 'meeting_request',
        categories: ['meeting_request'],
        messages: [
          { id: 'm33', sender: 'Rachel Green', text: 'Can you join the strategy meeting tomorrow?', time: '4:00 PM', sent: true, is_from_me: false },
          { id: 'm34', sender: 'You', text: 'Yes, count me in. What time?', time: '4:20 PM', sent: true, is_from_me: true },
          { id: 'm35', sender: 'Rachel Green', text: '2pm in the main conference room', time: '4:45 PM', sent: true, is_from_me: false },
          { id: 'm36', sender: 'Rachel Green', text: 'Conference room booked for 2pm tomorrow', time: '5:00 PM', sent: true, is_from_me: false }
        ]
      },
      {
        id: 'mock-10',
        name: 'Thomas White',
        type: 'text',
        folder: 'inbox',
        lastMessage: 'Budget approved! Let us kick off the project',
        timestamp: 'Dec 26, 2025 at 2:10 PM',
        unread: 0,
        avatar: 'TW',
        phone: '+1 (555) 012-3456',
        messageCategory: 'opportunity',
        category: 'opportunity',
        categories: ['opportunity'],
        messages: [
          { id: 'm37', sender: 'Thomas White', text: 'Great presentation yesterday! The team loved it', time: '11:00 AM', sent: true, is_from_me: false },
          { id: 'm38', sender: 'You', text: 'Thank you! I am excited about this partnership', time: '11:30 AM', sent: true, is_from_me: true },
          { id: 'm39', sender: 'Thomas White', text: 'We are finalizing the budget this afternoon', time: '1:45 PM', sent: true, is_from_me: false },
          { id: 'm40', sender: 'Thomas White', text: 'Budget approved! Let us kick off the project', time: '2:10 PM', sent: true, is_from_me: false }
        ]
      }
    ];
  };

  // Fetch conversations from backend
  const fetchConversations = async (silent = false) => {
    try {
      console.log('🔍 Loading mockup conversations...');
      
      // USE MOCKUP DATA INSTEAD OF API CALLS
      const allConversations = getMockupConversations();
      console.log('✅ Mockup conversations loaded:', allConversations.length);
      
      const conversationsWithCategory = allConversations.map(conv => ({
        ...conv,
        name: conv.name,
        originalName: conv.name,
        messageCategory: conv.category,
        categories: conv.categories || [conv.category].filter(Boolean)
      }));
      
      setConversations(conversationsWithCategory);
      console.log('✅ Conversations set in state:', conversationsWithCategory.length);
      
      // If a conversation is currently selected, update it with fresh data
      if (selectedConversation) {
        const updatedSelected = conversationsWithCategory.find(c => c.id === selectedConversation.id);
        if (updatedSelected) {
          setSelectedConversation(updatedSelected);
        }
      }
      
      setLoading(false);
      console.log('✅ Loading set to false');
    } catch (error) {
      console.error('❌ Error loading mockup conversations:', error);
      setErrorMessage(`Failed to load conversations: ${error.message}`);
      setLoading(false);
      setConversations([]);
    }
  };

  // Load scheduled messages
  const fetchScheduledMessages = async () => {
    try {
      const scheduled = await scheduledMessagesAPI.getAll('pending');
      setScheduledMessages(scheduled);
      console.log(`📅 Loaded ${scheduled.length} scheduled messages`);
    } catch (error) {
      console.error('Error loading scheduled messages:', error);
    }
  };

  // Scroll to bottom of messages when conversation changes
  useEffect(() => {
    if (selectedConversation && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedConversation?.id]);

  // Load conversations on mount
  useEffect(() => {
    // Show page shell immediately (instant render)
    requestAnimationFrame(() => {
      setInitialRender(true);
      setLoading(false); // Show UI immediately with empty state
    });
    
    // Load iCloud contacts first, then fetch conversations
    const initializeData = async () => {
      await loadIcloudContacts(); // Wait for contacts to load (from cache if available)
      fetchConversations(); // Then fetch conversations with contact matching
      fetchScheduledMessages();
    };
    
    initializeData();
    
    // Check if Outlook is connected
    if (outlookToken) {
      setOutlookConnected(true);
      fetchEmailFolders();
      fetchEmails();
    }
    
    // Poll for new messages every 15 seconds (optimized from 5 seconds)
    const pollInterval = setInterval(() => {
      fetchConversations(true); // Silent polling (no console logs) - will auto-match names
    }, 15000); // Poll every 15 seconds (balanced performance & real-time updates)
    
    // Check and send pending scheduled messages every 60 seconds
    const scheduledInterval = setInterval(async () => {
      try {
        const result = await scheduledMessagesAPI.sendPending();
        if (result.sent_count > 0) {
          console.log(`✅ Sent ${result.sent_count} scheduled messages`);
          fetchScheduledMessages();
          fetchConversations();
        }
      } catch (error) {
        console.error('Error sending scheduled messages:', error);
      }
    }, 60000); // Check every 60 seconds
    
    // Also check immediately on mount
    scheduledMessagesAPI.sendPending().then(result => {
      if (result.sent_count > 0) {
        console.log(`✅ Sent ${result.sent_count} scheduled messages on mount`);
        fetchScheduledMessages();
        fetchConversations();
      }
    }).catch(err => console.error('Error sending scheduled messages:', err));
    
    return () => {
      clearInterval(pollInterval);
      clearInterval(scheduledInterval);
    };
  }, []);

  // Re-process conversations when iCloud contacts are loaded to update names
  useEffect(() => {
    if (icloudContacts.length > 0 && conversations.length > 0) {
      console.log('📱 iCloud contacts loaded, updating conversation names...');
      
      const updatedConversations = conversations.map(conv => {
        let displayName = conv.name;
        
        // If conversation has a phone number and name is just the phone number or empty
        if (conv.phone && (!conv.name || conv.name === conv.phone || /^\+?\d[\d\s\-()]+$/.test(conv.name))) {
          const contactName = getContactNameByPhone(conv.phone);
          if (contactName && contactName !== conv.name) {
            displayName = contactName;
            console.log(`📱 Updated: ${conv.phone} → ${contactName}`);
          }
        }
        
        return {
          ...conv,
          name: displayName,
          originalName: conv.originalName || conv.name
        };
      });
      
      setConversations(updatedConversations);
    }
  }, [icloudContacts]);

  // Outlook connection functions
  const connectOutlook = async () => {
    try {
      const response = await outlookAPI.getAuthUrl();
      // Open OAuth window
      window.location.href = response.authorization_url;
    } catch (error) {
      console.error('Failed to connect Outlook:', error);
      alert('Failed to initiate Outlook connection');
    }
  };

  const fetchEmailFolders = async () => {
    if (!outlookToken) return;
    
    try {
      const response = await outlookAPI.getFolders(outlookToken);
      if (response.success) {
        setEmailFolders(response.folders);
      }
    } catch (error) {
      console.error('Failed to fetch email folders:', error);
    }
  };

  const fetchEmails = async (folderId = null) => {
    if (!outlookToken) return;
    
    setLoadingEmails(true);
    try {
      const response = await outlookAPI.getMessages(outlookToken, folderId, 25, 0);
      if (response.success) {
        setEmails(response.messages);
      }
    } catch (error) {
      console.error('Failed to fetch emails:', error);
      // If token expired, clear it
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        localStorage.removeItem('outlook_token');
        setOutlookToken(null);
        setOutlookConnected(false);
      }
    } finally {
      setLoadingEmails(false);
    }
  };


  // ============================================
  // OUTLOOK EMAIL INTEGRATION (using Calendar Auth)
  // ============================================
  
  // Check if Outlook email is connected (uses calendar auth)
  const checkOutlookEmailConnection = async () => {
    try {
      setCheckingOutlookConnection(true);
      const status = await outlookCalendarAuthAPI.status();
      const isAuthenticated = status.authenticated && status.user_email && status.user_email !== 'Unknown';
      
      setOutlookEmailConnected(isAuthenticated);
      
      if (isAuthenticated) {
        console.log(`✅ Outlook email connected: ${status.user_email}`);
        
        // Load synced emails from local database immediately
        try {
          const localEmails = await outlookEmailAPI.getLocalEmails('inbox');
          setOutlookEmails(localEmails.emails || []);
          console.log(`📧 Loaded ${localEmails.emails?.length || 0} emails from local database`);
        } catch (err) {
          console.error('Error loading local emails:', err);
        }
        
        // Trigger initial sync in background
        syncOutlookEmails().catch(console.error);
        
        // Set up 3-minute auto-sync for emails
        if (syncEmailIntervalRef.current) {
          clearInterval(syncEmailIntervalRef.current);
        }
        syncEmailIntervalRef.current = setInterval(async () => {
          console.log('Auto-syncing Outlook emails...');
          await syncOutlookEmails().catch(console.error);
        }, 3 * 60 * 1000); // 3 minutes
      }
    } catch (error) {
      console.error('Error checking Outlook email connection:', error);
      setOutlookEmailConnected(false);
    } finally {
      setCheckingOutlookConnection(false);
    }
  };
  
  // Sync emails from Outlook
  const syncOutlookEmails = async (folder = 'inbox') => {
    try {
      console.log(`Starting Outlook email sync for ${folder}...`);
      setIsSyncingEmails(true);
      
      const response = await outlookEmailAPI.syncEmails(folder, 100);
      console.log('Email sync response:', response);
      
      // Fetch synced emails from local database
      const localEmails = await outlookEmailAPI.getLocalEmails(folder);
      setOutlookEmails(localEmails.emails || []);
      setLastEmailSyncTime(new Date());
      
      console.log(`✅ Synced ${response.synced_count || 0} emails from ${folder}`);
    } catch (error) {
      console.error('❌ Error syncing Outlook emails:', error);
      
      if (error.message && (error.message.includes('401') || error.message.includes('403'))) {
        alert('❌ Email authentication error!\n\nPlease reconnect your Outlook account with email permissions.');
        setOutlookEmailConnected(false);
      } else {
        console.error('Sync error:', error.message || 'Unknown error');
      }
    } finally {
      setIsSyncingEmails(false);
    }
  };
  
  // Connect Outlook Email
  const handleConnectOutlookEmail = async () => {
    try {
      console.log('Initiating Outlook email connection...');
      const result = await outlookCalendarAuthAPI.login();
      
      if (result.auth_url) {
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        
        const popup = window.open(
          result.auth_url,
          'Outlook OAuth',
          `width=${width},height=${height},left=${left},top=${top}`
        );
        
        const checkInterval = setInterval(async () => {
          if (popup && popup.closed) {
            clearInterval(checkInterval);
            console.log('OAuth popup closed, checking connection...');
            
            await new Promise(resolve => setTimeout(resolve, 2000));
            await checkOutlookEmailConnection();
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error connecting Outlook email:', error);
      alert('❌ Failed to connect Outlook email. Please try again.');
    }
  };
  
  // Disconnect Outlook Email
  const handleDisconnectOutlookEmail = () => {
    if (window.confirm('Are you sure you want to disconnect Outlook email? Synced emails will remain in the database.')) {
      if (syncEmailIntervalRef.current) {
        clearInterval(syncEmailIntervalRef.current);
        syncEmailIntervalRef.current = null;
      }
      
      setOutlookEmailConnected(false);
      setOutlookEmails([]);
      setLastEmailSyncTime(null);
      
      alert('✅ Disconnected from Outlook email.');
      console.log('Outlook email disconnected');
    }
  };
  
  // Send email via Outlook
  const handleSendOutlookEmail = async (emailData) => {
    try {
      const response = await outlookEmailAPI.sendEmail(emailData);
      console.log('✅ Email sent:', response);
      
      // Trigger sync to update local database
      await syncOutlookEmails('sent');
      
      return response;
    } catch (error) {
      console.error('❌ Failed to send email:', error);
      throw error;
    }
  };
  
  // Use effect to check Outlook email connection on mount
  useEffect(() => {
    checkOutlookEmailConnection();
    
    return () => {
      if (syncEmailIntervalRef.current) {
        clearInterval(syncEmailIntervalRef.current);
      }
    };
  }, []);
  
  // Pre-populate CC/BCC when Outlook email is selected
  useEffect(() => {
    if (selectedConversation?.type === 'outlook_email' && selectedConversation.email) {
      const email = selectedConversation.email;
      
      // Pre-populate CC if exists
      if (email.cc && email.cc.length > 0) {
        setEmailCc(email.cc.join(', '));
        setShowCcBcc(true);
      } else {
        setEmailCc('');
      }
      
      // Pre-populate BCC if exists
      if (email.bcc && email.bcc.length > 0) {
        setEmailBcc(email.bcc.join(', '));
        setShowCcBcc(true);
      } else {
        setEmailBcc('');
      }
    }
  }, [selectedConversation]);

  // Handle file attachment
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    // Convert files to base64 for storage
    const processFiles = async () => {
      const processedFiles = await Promise.all(
        files.map(file => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                name: file.name,
                contentType: file.type,
                size: file.size,
                contentBytes: e.target.result.split(',')[1] // Remove data:... prefix
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );
      
      setEmailAttachments([...emailAttachments, ...processedFiles]);
    };
    
    processFiles().catch(error => {
      console.error('Error processing files:', error);
      alert('Failed to process attachments');
    });
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Update group chat name
  const handleUpdateGroupName = async () => {
    if (!editedGroupName.trim() || !selectedConversation) return;
    
    try {
      await conversationsAPI.update(selectedConversation.id, {
        group_name: editedGroupName.trim()
      });
      
      // Update local state
      setSelectedConversation({
        ...selectedConversation,
        group_name: editedGroupName.trim()
      });
      
      setConversations(conversations.map(conv =>
        conv.id === selectedConversation.id
          ? { ...conv, group_name: editedGroupName.trim() }
          : conv
      ));
      
      setIsEditingGroupName(false);
      setEditedGroupName('');
    } catch (error) {
      console.error('Error updating group name:', error);
      alert('Failed to update group name');
    }
  };

  // Fetch conversations on component mount


  const disconnectOutlook = () => {
    localStorage.removeItem('outlook_token');
    setOutlookToken(null);
    setOutlookConnected(false);
    setEmailFolders([]);
    setEmails([]);
  };

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      handleOAuthCallback(code);
    }
  }, []);

  const handleOAuthCallback = async (code) => {
    try {
      const response = await outlookAPI.handleCallback(code);
      if (response.success && response.access_token) {
        localStorage.setItem('outlook_token', response.access_token);
        if (response.refresh_token) {
          localStorage.setItem('outlook_refresh_token', response.refresh_token);
        }
        setOutlookToken(response.access_token);
        setOutlookConnected(true);
        
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Fetch folders and emails
        fetchEmailFolders();
        fetchEmails();
      }
    } catch (error) {
      console.error('OAuth callback failed:', error);
      alert('Failed to connect Outlook. Please try again.');
    }
  };

  // Poll for new messages every 5 seconds (duplicate polling already set up above, removing this one)
  // This was causing double polling - the main polling is in the useEffect at line 242

  // Helper function to count unread messages in a category
  const countUnreadInCategory = (category) => {
    return conversations.filter(conv => {
      // Check if conversation is unread AND has messages with this category
      if (conv.unread > 0) {
        const categories = conv.categories || [conv.category];
        return categories.includes(category);
      }
      return false;
    }).length;
  };

  const folders = [
    { id: 'all', name: 'All Messages', color: 'bg-blue-500', count: conversations.filter(c => c.unread > 0).length },
    { id: 'meeting_request', name: 'Meeting Requests', color: 'bg-yellow-500', count: countUnreadInCategory('meeting_request') },
    { id: 'introduction', name: 'Introductions', color: 'bg-green-500', count: countUnreadInCategory('introduction') },
    { id: 'urgent', name: 'Urgent', color: 'bg-red-500', count: countUnreadInCategory('urgent') },
    { id: 'opportunity', name: 'Opportunities', color: 'bg-purple-500', count: countUnreadInCategory('opportunity') },
    { id: 'travel', name: 'Travel', color: 'bg-orange-500', count: countUnreadInCategory('travel') },
    { id: 'general', name: 'General', color: 'bg-gray-500', count: countUnreadInCategory('general') }
  ];

  // Helper function to get display name for keyword-based categories
  const getCategoryDisplayName = (category) => {
    const displayNames = {
      'meeting_request': 'Meeting Request',
      'introduction': 'Introduction',
      'urgent': 'Urgent',
      'opportunity': 'Opportunity',
      'general': 'General',
      // Business categories (if they still exist on old conversations)
      'cresa': 'Cresa',
      'software': 'Counterfind',
      'personal': 'Personal',
      'speaking': 'Speaking',
      'investment': 'Investment'
    };
    return displayNames[category] || category;
  };

  // Helper function to get category badge styling
  const getCategoryBadgeStyle = (category) => {
    const styles = {
      'meeting_request': 'bg-white/20 text-white',
      'introduction': 'bg-green-500/20 text-green-400',
      'urgent': 'bg-red-500/20 text-red-400',
      'opportunity': 'bg-purple-500/20 text-purple-400',
      'general': 'bg-transparent text-white',
      // Business categories fallback
      'cresa': 'bg-white/20 text-white',
      'software': 'bg-green-500/20 text-green-400',
      'personal': 'bg-purple-500/20 text-purple-400',
      'speaking': 'bg-orange-500/20 text-orange-400',
      'investment': 'bg-white/20 text-cyan-400'
    };
    return styles[category] || 'bg-transparent text-white';
  };

  const handleRegenerateDraft = async () => {
    if (!selectedConversation) return;
    
    setRegeneratingDraft(true);
    try {
      const response = await conversationsAPI.regenerateDraft(selectedConversation.id);
      setMessageText(response.draft_response);
      setShowDraft(true);
      
      // Update conversation in state
      setConversations(prev => prev.map(c => 
        c.id === selectedConversation.id 
          ? { ...c, draft_response: response.draft_response }
          : c
      ));
      
      setSelectedConversation({
        ...selectedConversation,
        draft_response: response.draft_response
      });
    } catch (error) {
      console.error('Failed to regenerate draft:', error);
      alert('Failed to regenerate draft. Please try again.');
    } finally {
      setRegeneratingDraft(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) {
      alert('Please enter a message');
      return;
    }

    try {
      if (selectedConversation.type === 'mac_bridge') {
        // Send via Mac Messages Bridge
        const recipient = selectedConversation.participants?.[0] || selectedConversation.name;
        const response = await macMessagesBridgeAPI.sendMessage(recipient, messageText.trim());
        
        if (response.success) {
          // Add pending message to conversation display
          const newMessage = {
            id: `pending-${Date.now()}`,
            sender: 'me',
            text: messageText.trim(),
            time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
            sent: true,
            is_from_me: true,
            pending: true
          };
          
          setSelectedConversation(prev => ({
            ...prev,
            messages: [...(prev.messages || []), newMessage]
          }));
          
          setMessageText('');
          alert('📤 Message queued! It will be sent when your Mac bridge picks it up.');
        } else {
          throw new Error('Failed to queue message');
        }
        
      } else if (selectedConversation.type === 'text') {
        // Send SMS via business phone
        const smsData = {
          message: messageText.trim(),
          phone_number: selectedConversation.phone,
          recipient_name: selectedConversation.name
        };

        if (selectedConversation.contact_id) {
          smsData.contact_id = selectedConversation.contact_id;
        }

        const response = await messagesAPI.sendSMS(smsData);
        
        // Add message to conversation display
        const newMessage = {
          id: response.twilio_sid || `msg-${Date.now()}`,
          sender: 'You',
          text: messageText.trim(),
          time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Chicago' }),
          sent: true
        };

        // Update conversation in database with new message
        const updatedMessages = [...(selectedConversation.messages || []), newMessage];
        
        await conversationsAPI.update(selectedConversation.id, {
          messages: updatedMessages,
          lastMessage: messageText.trim().substring(0, 50),
          timestamp: newMessage.time
        });

        // Update conversation messages in local state
        setSelectedConversation(prev => ({
          ...prev,
          messages: updatedMessages,
          lastMessage: messageText.trim().substring(0, 50),
          timestamp: newMessage.time
        }));

        // Update in conversations list
        setConversations(prev => prev.map(c =>
          c.id === selectedConversation.id
            ? { ...c, messages: updatedMessages, lastMessage: messageText.trim().substring(0, 50), timestamp: newMessage.time }
            : c
        ));

        setMessageText('');
        setShowDraft(false);
        
      } else if (selectedConversation.type === 'personal_phone') {
        // Send via personal iPhone
        await handleSendViaIPhone();
        setShowDraft(false);
        
      } else if (selectedConversation.type === 'email') {
        // Send Email
        alert('Email sending not yet implemented');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  // Send message via iPhone (for Personal Phone conversations)
  const handleSendViaIPhone = async () => {
    if (!messageText.trim() || !selectedConversation) return;

    // Check if Pushcut is configured
    if (!pushcutSecret) {
      const userResponse = confirm(
        '📱 Pushcut Not Configured\n\n' +
        'To send texts via iPhone, you need to configure Pushcut.\n\n' +
        'Click OK to go to the Pushcut setup page, or Cancel to continue.'
      );
      
      if (userResponse) {
        window.location.href = '/pushcut-test';
      }
      return;
    }

    setSendingViaPushcut(true);
    setPushcutResult(null);

    try {
      // Determine contact - use phone number or name
      const contact = selectedConversation.phone || selectedConversation.name;
      
      // Send via Pushcut
      const response = await pushcutAPI.sendText(
        contact,
        messageText,
        pushcutSecret,
        'Send Dashboard Text'
      );

      console.log('✅ Pushcut webhook sent:', response);
      
      setPushcutResult({ success: true, message: response.message });
      
      // Show success message
      alert(`📱 Text sent via iPhone!\n\n${response.message}\n\nCheck your iPhone - message should be sending now.`);
      
      // Clear input
      setMessageText('');
      
      // Add a "pending" message to the conversation
      const pendingMessage = {
        id: `msg-pending-${Date.now()}`,
        sender: 'You',
        text: messageText,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        sent: false,
        pending: true,
        viaPushcut: true
      };
      
      const updatedConv = {
        ...selectedConversation,
        messages: [...(selectedConversation.messages || []), pendingMessage],
        lastMessage: messageText,
        timestamp: 'Just now'
      };
      
      setSelectedConversation(updatedConv);
      setConversations(prev => 
        prev.map(c => c.id === selectedConversation.id ? updatedConv : c)
      );
      
    } catch (error) {
      console.error('Failed to send via Pushcut:', error);
      setPushcutResult({ success: false, message: error.message || 'Failed to send text via iPhone' });
      alert(`❌ Failed to send via iPhone\n\n${error.message}\n\nPlease check your Pushcut configuration.`);
    } finally {
      setSendingViaPushcut(false);
    }
  };

  // Send message via Mac Messages app (for text conversations)
  const handleSendViaMacInConversation = async () => {
    if (!messageText.trim() || !selectedConversation) {
      alert('Please enter a message');
      return;
    }

    setSendingViaMac(true);
    
    try {
      const recipient = selectedConversation.phone || selectedConversation.name;
      
      // Use the Mac Messages Bridge queue system instead of direct AppleScript
      const response = await macMessagesBridgeAPI.sendMessage(recipient, messageText.trim());

      if (response.success) {
        console.log('✅ Message queued for Mac bridge:', response);
        
        const newMessage = {
          id: `msg-${Date.now()}`,
          sender: 'You',
          text: messageText.trim(),
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          sent: true,
          pending: true // Mark as pending until Mac bridge confirms
        };

        const updatedMessages = [...(selectedConversation.messages || []), newMessage];
        
        // Only try to update regular conversations, not Mac bridge ones
        if (selectedConversation.type !== 'mac_bridge') {
          try {
            await conversationsAPI.update(selectedConversation.id, {
              messages: updatedMessages,
              lastMessage: messageText.trim().substring(0, 50),
              timestamp: newMessage.time
            });
          } catch (updateError) {
            console.log('Skipping conversation update for Mac bridge message');
          }
        }

        setSelectedConversation(prev => ({
          ...prev,
          messages: updatedMessages,
          lastMessage: messageText.trim().substring(0, 50),
          timestamp: newMessage.time
        }));

        setConversations(prev => prev.map(c =>
          c.id === selectedConversation.id
            ? { ...c, messages: updatedMessages, lastMessage: messageText.trim().substring(0, 50), timestamp: newMessage.time }
            : c
        ));

        setMessageText('');
        setShowDraft(false);
        
        alert(`📤 Message queued! Your Mac bridge will send it to ${selectedConversation.name} shortly.`);
      } else {
        throw new Error('Failed to queue message');
      }
      
    } catch (error) {
      console.error('Failed to send via Mac:', error);
      alert(`❌ Failed to queue message\n\n${error.message}\n\nMake sure your Mac Messages Bridge is running.`);
    } finally {
      setSendingViaMac(false);
    }
  };

  const handleScheduleMessage = async () => {
    if (!scheduleDateTime || !messageToSchedule.trim()) {
      alert('Please enter a message and select a date/time');
      return;
    }

    try {
      let scheduleData;
      
      // Check if scheduling from existing conversation or Create Message form
      if (selectedConversation) {
        // Scheduling from existing conversation
        scheduleData = {
          conversation_id: selectedConversation.id,
          recipient_phone: selectedConversation.phone,
          recipient_name: selectedConversation.name,
          message_text: messageToSchedule.trim(),
          scheduled_time: new Date(scheduleDateTime).toISOString(),
          is_draft_response: isDraftSchedule
        };
      } else if (selectedContact) {
        // Scheduling from Create Message form
        const recipientPhone = useManualEntry ? manualPhone : selectedContact.phone;
        const recipientName = useManualEntry ? manualName : selectedContact.name;
        
        // Create a temporary conversation ID or use existing
        const conversations = await conversationsAPI.getAll();
        let conversation = conversations.find(c => c.phone === recipientPhone);
        
        if (!conversation) {
          // Create a new conversation first
          const newConv = await conversationsAPI.create({
            name: recipientName,
            type: messageType === 'sms' ? 'text' : 'email',
            folder: 'inbox',
            lastMessage: messageToSchedule.substring(0, 50),
            timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Chicago' }),
            unread: 0,
            avatar: `https://ui-avatars.com/api/?name=${recipientName.replace(' ', '+')}`,
            phone: recipientPhone,
            email: messageType === 'email' ? selectedContact.email : null,
            messages: []
          });
          conversation = newConv;
        }
        
        scheduleData = {
          conversation_id: conversation.id,
          recipient_phone: recipientPhone,
          recipient_name: recipientName,
          message_text: messageToSchedule.trim(),
          scheduled_time: new Date(scheduleDateTime).toISOString(),
          is_draft_response: false
        };
      } else {
        alert('Please select a recipient');
        return;
      }

      await scheduledMessagesAPI.create(scheduleData);
      
      alert(`Message scheduled for ${new Date(scheduleDateTime).toLocaleString('en-US', { timeZone: 'America/Chicago' })}`);
      
      // Clear form and close dialogs
      setScheduleDateTime('');
      setMessageToSchedule('');
      setIsDraftSchedule(false);
      setIsScheduleDialogOpen(false);
      
      // If scheduling from Create Message form, also close that
      if (!selectedConversation && selectedContact) {
        setIsComposeOpen(false);
        setComposeMessage('');
        setSelectedContact(null);
      }
      
      // If scheduling the current draft, clear the message input
      if (isDraftSchedule && selectedConversation) {
        setMessageText('');
        setShowDraft(false);
      }
    } catch (error) {
      console.error('Failed to schedule message:', error);
      alert('Failed to schedule message. Please try again.');
    }
  };

  const handleOpenScheduleDialog = (useDraft = false) => {
    if (useDraft && showDraft) {
      setMessageToSchedule(messageText);
      setIsDraftSchedule(true);
    } else {
      setMessageToSchedule(messageText || '');
      setIsDraftSchedule(false);
    }
    
    // Set default schedule time to 1 hour from now in CST
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    const localDateTime = oneHourLater.toISOString().slice(0, 16);
    setScheduleDateTime(localDateTime);
    
    setIsScheduleDialogOpen(true);
  };

  const handleSendViaMacCompose = async () => {
    // Validate inputs
    let recipientName, recipientContact;
    
    if (useManualEntry) {
      if (!manualName.trim()) {
        alert('Please enter a name');
        return;
      }
      if (!manualPhone.trim()) {
        alert('Please enter a phone number');
        return;
      }
      recipientName = manualName;
      recipientContact = manualPhone;
    } else {
      if (!selectedContact) {
        alert('Please select a contact');
        return;
      }
      const contact = contacts.find(c => c.id === selectedContact) || icloudContacts.find(c => c.id === selectedContact);
      if (!contact) {
        alert('Contact not found');
        return;
      }
      recipientName = contact.name;
      recipientContact = contact.phone || contact.name;
    }

    if (!composeMessage.trim()) {
      alert('Please enter a message');
      return;
    }

    setSending(true);
    
    try {
      // Use the Mac Messages Bridge queue system
      const response = await macMessagesBridgeAPI.sendMessage(recipientContact, composeMessage.trim());

      if (response.success) {
        console.log('✅ Message queued for Mac bridge:', response);
        alert(`📤 Message queued for ${recipientName}!\n\nYour Mac bridge will send it shortly.`);
        
        // Clear form and close dialog
        setComposeMessage('');
        setSelectedContact('');
        setManualPhone('');
        setManualName('');
        setManualEmail('');
        setIsComposeOpen(false);
      } else {
        throw new Error('Failed to queue message');
      }
      
    } catch (error) {
      console.error('Failed to queue message:', error);
      alert(`❌ Failed to queue message\n\n${error.message}\n\nMake sure your Mac Messages Bridge is running.`);
    } finally {
      setSending(false);
    }
  };

  const handleSendViaPushcutCompose = async () => {
    // Validate inputs
    let recipientName, recipientContact;
    
    if (useManualEntry) {
      if (!manualName.trim()) {
        alert('Please enter a name');
        return;
      }
      if (!manualPhone.trim()) {
        alert('Please enter a phone number');
        return;
      }
      recipientName = manualName;
      recipientContact = manualPhone;
    } else {
      if (!selectedContact) {
        alert('Please select a contact');
        return;
      }
      const contact = contacts.find(c => c.id === selectedContact) || icloudContacts.find(c => c.id === selectedContact);
      if (!contact) {
        alert('Contact not found');
        return;
      }
      recipientName = contact.name;
      recipientContact = contact.phone || contact.name; // Use phone or fallback to name
    }

    if (!composeMessage.trim()) {
      alert('Please enter a message');
      return;
    }

    // Check Pushcut configuration
    if (!pushcutSecret) {
      const userResponse = confirm(
        '📱 Pushcut Not Configured\n\n' +
        'To send texts via iPhone, you need to configure Pushcut.\n\n' +
        'Click OK to go to the Pushcut setup page.'
      );
      
      if (userResponse) {
        window.location.href = '/pushcut-test';
      }
      return;
    }

    setSending(true);
    
    try {
      const response = await pushcutAPI.sendText(
        recipientContact,
        composeMessage,
        pushcutSecret,
        'Send Dashboard Text'
      );

      console.log('✅ Message sent via Pushcut:', response);
      alert(`📱 Text sent via iPhone to ${recipientName}!\n\nCheck your iPhone.`);
      
      // Clear form and close dialog
      setComposeMessage('');
      setSelectedContact('');
      setManualPhone('');
      setManualName('');
      setManualEmail('');
      setIsComposeOpen(false);
      
    } catch (error) {
      console.error('Failed to send via Pushcut:', error);
      alert(`❌ Failed to send via iPhone\n\n${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleSendNewMessage = async () => {
    let recipientName, recipientContact, recipientId, contact;
    
    // Validate based on mode
    if (useManualEntry) {
      if (!manualName.trim()) {
        alert('Please enter a name');
        return;
      }
      if (messageType === 'sms' && !manualPhone.trim()) {
        alert('Please enter a phone number for SMS');
        return;
      }
      if (messageType === 'email' && !manualEmail.trim()) {
        alert('Please enter an email address');
        return;
      }
      recipientName = manualName;
      recipientContact = messageType === 'sms' ? manualPhone : manualEmail;
      recipientId = null;
      contact = null;
    } else {
      if (!selectedContact) {
        alert('Please select a contact');
        return;
      }
      contact = contacts.find(c => c.id === selectedContact);
      if (!contact) {
        alert('Contact not found');
        return;
      }
      recipientName = contact.name;
      recipientContact = messageType === 'sms' ? contact.phone : contact.email;
      recipientId = contact.id;
    }

    if (!composeMessage.trim()) {
      alert('Please enter a message');
      return;
    }

    setSending(true);
    
    try {
      if (messageType === 'sms') {
        // Send SMS via Twilio - works for both contact selector and manual entry
        if (useManualEntry) {
          // Manual entry - send with phone number directly
          await messagesAPI.sendSMS({
            phone_number: manualPhone,
            recipient_name: manualName,
            message: composeMessage
          });
        } else {
          // Contact selector - send with contact_id
          await messagesAPI.sendSMS({
            contact_id: selectedContact,
            message: composeMessage
          });
        }
        
        // Check if conversation already exists for this contact/phone
        let existingConversation = null;
        
        if (recipientId) {
          // Check by contact_id first
          existingConversation = conversations.find(conv => conv.contact_id === recipientId);
        }
        
        if (!existingConversation && recipientContact) {
          // Check by phone number
          existingConversation = conversations.find(conv => conv.phone === recipientContact);
        }
        
        if (existingConversation) {
          // Update existing conversation with new message
          console.log(`📱 Adding message to existing conversation with ${recipientName}`);
          
          const newMessage = {
            id: `msg-${Date.now()}`,
            sender: 'You',
            text: composeMessage,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            sent: true
          };
          
          // Update conversation
          const updatedConversation = {
            ...existingConversation,
            lastMessage: composeMessage,
            timestamp: 'Just now',
            messages: [...(existingConversation.messages || []), newMessage]
          };
          
          // Update in backend
          await conversationsAPI.update(existingConversation.id, updatedConversation);
          
          // Update in state
          setConversations(prev => 
            prev.map(conv => conv.id === existingConversation.id ? updatedConversation : conv)
          );
          setSelectedConversation(updatedConversation);
          
          alert('SMS sent successfully!');
        } else {
          // Create new conversation object
          console.log(`📱 Creating new conversation with ${recipientName}`);
          
          const newConversationData = {
            name: recipientName,
            type: 'text',
            folder: 'all',
            lastMessage: composeMessage,
            timestamp: 'Just now',
            unread: 0,
            avatar: recipientName.split(' ').map(n => n[0]).join('').toUpperCase(),
            contact_id: recipientId,
            phone: messageType === 'sms' ? recipientContact : null,
            email: messageType === 'email' ? recipientContact : null,
            messages: [
              {
                id: `msg-${Date.now()}`,
                sender: 'You',
                text: composeMessage,
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                sent: true
              }
            ]
          };
          
          // Save to backend
          const savedConversation = await conversationsAPI.create(newConversationData);
          
          // Add to conversations and select it
          setConversations(prev => [savedConversation, ...prev]);
          setSelectedConversation(savedConversation);
          
          alert('SMS sent successfully!');
        }
      } else {
        // Email functionality - send via Outlook
        if (!outlookToken) {
          alert('Please connect your Outlook account first');
          return;
        }
        
        if (!composeSubject.trim()) {
          alert('Please enter an email subject');
          return;
        }
        
        const emailRecipients = useManualEntry ? [manualEmail] : [contact.email];
        
        await outlookAPI.sendEmail(outlookToken, {
          to_recipients: emailRecipients,
          subject: composeSubject,
          body: composeMessage,
          is_html: false
        });
        
        alert('Email sent successfully!');
        
        // Refresh emails
        fetchEmails();
      }
      
      // Reset form
      setSelectedContact('');
      setManualPhone('');
      setManualEmail('');
      setManualName('');
      setComposeMessage('');
      setComposeSubject('');
      setUseManualEntry(false);
      setIsComposeOpen(false);
      
    } catch (error) {
      console.error('Error sending message:', error);
      alert(`Failed to send message: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      try {
        // Find the conversation to check its type
        const conv = conversations.find(c => c.id === conversationId);
        
        // Use appropriate API based on conversation type
        if (conv?.type === 'personal_phone') {
          await personalPhoneAPI.deleteConversation(conversationId);
        } else {
          await conversationsAPI.delete(conversationId);
        }
        
        // Remove from state
        setConversations(prev => prev.filter(c => c.id !== conversationId));
        
        // Clear selection if this was selected
        if (selectedConversation?.id === conversationId) {
          setSelectedConversation(null);
        }
        
        console.log('✅ Conversation deleted successfully');
      } catch (error) {
        console.error('❌ Error deleting conversation:', error);
        alert('Failed to delete conversation. Please try again.');
      }
    }
  };

  // Handle clicking on contact name to edit
  const handleEditContact = async (conversation) => {
    if (!conversation.contact_id) {
      alert('This contact is not in your contacts list');
      return;
    }
    
    try {
      const contact = await contactsAPI.getById(conversation.contact_id);
      setEditingContact(contact);
      setContactFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        company: contact.company || '',
        role: contact.role || '',
        category: contact.category || '',
        notes: contact.notes || '',
        image: contact.image || ''
      });
      setIsEditContactOpen(true);
    } catch (error) {
      console.error('Error fetching contact:', error);
      alert('Failed to load contact details');
    }
  };

  // Add contact from unknown number
  const handleAddContactFromConversation = (conversation) => {
    setEditingContact(null); // Clear editing mode
    setContactFormData({
      name: conversation.name || '',
      email: conversation.email || '',
      phone: conversation.phone || '',
      company: '',
      role: '',
      category: 'personal',
      notes: `Added from ${conversation.type === 'email' ? 'email' : 'text message'}`,
      image: ''
    });
    setIsEditContactOpen(true);
  };

  // Save edited contact
  const handleSaveContact = async () => {
    // Validate required fields
    if (!contactFormData.name || !contactFormData.name.trim()) {
      alert('Please enter a contact name');
      return;
    }
    
    try {
      console.log('📇 Saving contact:', contactFormData);
      
      if (editingContact) {
        // Update existing contact
        await contactsAPI.update(editingContact.id, contactFormData);
        alert('Contact updated successfully!');
        
        // Update conversation in database with new contact name and category
        if (selectedConversation && selectedConversation.contact_id === editingContact.id) {
          await conversationsAPI.update(selectedConversation.id, {
            name: contactFormData.name
          });
        }
        
        // Update local state
        setConversations(prev => prev.map(conv => 
          conv.contact_id === editingContact.id 
            ? { ...conv, name: contactFormData.name, contactCategory: contactFormData.category }
            : conv
        ));
        
        // Update selected conversation if it's the one being edited
        if (selectedConversation && selectedConversation.contact_id === editingContact.id) {
          setSelectedConversation(prev => ({
            ...prev,
            name: contactFormData.name,
            contactCategory: contactFormData.category
          }));
        }
      } else {
        // Create new contact - clean up the data
        const cleanedData = {
          name: contactFormData.name.trim(),
          email: contactFormData.email?.trim() || null,
          phone: contactFormData.phone?.trim() || null,
          company: contactFormData.company?.trim() || null,
          role: contactFormData.role?.trim() || null,
          category: contactFormData.category || null,
          notes: contactFormData.notes?.trim() || null,
          image: contactFormData.image || null
        };
        
        console.log('📇 Creating contact with cleaned data:', cleanedData);
        const newContact = await contactsAPI.create(cleanedData);
        console.log('📇 Contact created:', newContact);
        alert('Contact added successfully!');
        
        // Update conversation in database with new contact_id and name
        if (selectedConversation) {
          try {
            await conversationsAPI.update(selectedConversation.id, {
              contact_id: newContact.id,
              name: contactFormData.name
            });
          } catch (convError) {
            console.warn('Could not update conversation:', convError);
          }
          
          // Update local state
          setSelectedConversation(prev => ({
            ...prev,
            contact_id: newContact.id,
            name: contactFormData.name,
            contactCategory: contactFormData.category
          }));
          
          // Update in conversations list
          setConversations(prev => prev.map(conv =>
            conv.id === selectedConversation.id
              ? { ...conv, contact_id: newContact.id, name: contactFormData.name, contactCategory: contactFormData.category }
              : conv
          ));
          
          // Refetch conversations to ensure data is in sync with database
          await fetchConversations();
        }
      }
      
      setIsEditContactOpen(false);
      setEditingContact(null);
    } catch (error) {
      console.error('Error saving contact:', error);
      const errorMsg = error.message || error.detail || 'Unknown error';
      alert(`Failed to ${editingContact ? 'update' : 'add'} contact: ${errorMsg}`);
    }
  };

  // Insert link into message
  const handleInsertLink = () => {
    if (!linkUrl.trim()) {
      alert('Please enter a URL');
      return;
    }
    
    const linkMarkdown = linkText.trim() 
      ? `[${linkText}](${linkUrl})` 
      : linkUrl;
    
    setMessageText(prev => prev + linkMarkdown);
    setLinkUrl('');
    setLinkText('');
    setShowLinkDialog(false);
  };

  // Common emojis
  const emojis = ['😊', '😂', '❤️', '👍', '🎉', '🔥', '✅', '💯', '🙏', '👏', '💪', '🎯', '📱', '💼', '🏠', '🚗', '✈️', '⭐', '💡', '🎁'];

  const handleEmojiSelect = (emoji) => {
    setMessageText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Load scheduled messages for current conversation
  const loadConversationScheduledMessages = async (conversationId) => {
    try {
      const allScheduled = await scheduledMessagesAPI.getAll('pending');
      const convScheduled = allScheduled.filter(msg => msg.conversation_id === conversationId);
      setConversationScheduledMsgs(convScheduled);
      console.log(`📅 Loaded ${convScheduled.length} scheduled messages for conversation`);
    } catch (error) {
      console.error('Error loading conversation scheduled messages:', error);
      setConversationScheduledMsgs([]);
    }
  };

  const handleSelectConversation = async (conv) => {
    // Handle Mac Bridge conversations specially - load messages from the bridge API
    if (conv.type === 'mac_bridge') {
      setLoadingMacBridge(true);
      try {
        const msgData = await macMessagesBridgeAPI.getMessages(conv.chat_id);
        const messagesWithFormatting = (msgData.messages || []).map(msg => ({
          ...msg,
          id: msg.message_id,
          sender: msg.is_from_me ? 'You' : (msg.sender || conv.name),
          text: msg.text || '',
          time: msg.timestamp,
          sent: msg.is_from_me,
          category: msg.category,
          attachments: msg.attachments || [] // Include attachments
        }));
        
        setSelectedConversation({
          ...conv,
          messages: messagesWithFormatting,
          messageCategory: conv.messageCategory || conv.category
        });
      } catch (error) {
        console.error('Error loading Mac messages:', error);
        setSelectedConversation(conv);
      }
      setLoadingMacBridge(false);
      return;
    }
    
    // Mark as read if it has unread messages
    if (conv.unread > 0) {
      try {
        // Use appropriate API based on conversation type
        if (conv.type === 'personal_phone') {
          await personalPhoneAPI.markAsRead(conv.id);
        } else {
          // Mark regular conversations as read
          await conversationsAPI.markAsRead(conv.id);
        }
        
        // Update the conversation in state to clear unread badge
        setConversations(prev => 
          prev.map(c => c.id === conv.id ? { ...c, unread: 0 } : c)
        );
        
        // Update conv object for setting selected conversation
        conv = { ...conv, unread: 0 };
        
        // Dispatch event to notify Navbar to refresh counts
        window.dispatchEvent(new CustomEvent('unreadCountChanged'));
        
        console.log(`✅ Marked conversation as read: ${conv.name}`);
      } catch (error) {
        console.error('Failed to mark conversation as read:', error);
      }
    }
    
    // If conversation has a contact_id, fetch contact details to get contact category
    if (conv.contact_id) {
      try {
        const contact = await contactsAPI.getById(conv.contact_id);
        setSelectedConversation({ 
          ...conv, 
          messageCategory: conv.messageCategory || conv.category,
          contactCategory: contact.category 
        });
      } catch (error) {
        console.error('Failed to fetch contact details:', error);
        setSelectedConversation({
          ...conv,
          messageCategory: conv.messageCategory || conv.category
        });
      }
    } else {
      setSelectedConversation({
        ...conv,
        messageCategory: conv.messageCategory || conv.category
      });
    }
    
    // Load scheduled messages for this conversation
    loadConversationScheduledMessages(conv.id);
    
    // Show draft if available and it's a text or personal_phone conversation with a message category
    const msgCategory = conv.messageCategory || conv.category;
    if (conv.draft_response && (conv.type === 'text' || conv.type === 'personal_phone') && msgCategory && msgCategory !== 'general') {
      setShowDraft(true);
      setMessageText(conv.draft_response);
    } else {
      setShowDraft(false);
      setMessageText('');
    }
    
    // Mark as read if it has unread messages
    if (conv.unread > 0) {
      try {
        await conversationsAPI.markAsRead(conv.id);
        // Update local state
        setConversations(prev => prev.map(c => 
          c.id === conv.id ? { ...c, unread: 0 } : c
        ));
        
        // Dispatch custom event to update sidebar counts
        window.dispatchEvent(new CustomEvent('unreadCountChanged'));
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  // Check if we're viewing individual messages (keyword folder selected, not 'all')
  const isViewingMessages = selectedFolder !== 'all' && 
    ['meeting_request', 'introduction', 'urgent', 'opportunity', 'travel', 'general'].includes(selectedFolder);

  // Extract all individual messages with the selected category
  const filteredMessages = isViewingMessages ? (() => {
    const allMessages = [];
    
    conversations.forEach(conv => {
      // Filter by type first
      let typeMatch = true;
      if (activeTab === 'texts') typeMatch = conv.type === 'text';
      if (activeTab === 'emails') typeMatch = conv.type === 'email';
      if (activeTab === 'personal') typeMatch = conv.type === 'personal_phone';
      
      if (typeMatch && conv.messages) {
        conv.messages.forEach((msg, msgIndex) => {
          if (msg.category === selectedFolder) {
            // Search filter
            const searchMatch = searchQuery.trim() === '' || 
              conv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              msg.text.toLowerCase().includes(searchQuery.toLowerCase());
            
            if (searchMatch) {
              allMessages.push({
                ...msg,
                conversationId: conv.id,
                conversationName: conv.name,
                conversationType: conv.type,
                conversationAvatar: conv.avatar,
                conversation: conv,  // Store full conversation for opening
                conversationTimestamp: conv.updated_at || conv.timestamp,  // Use updated_at first for full date, fallback to timestamp
                messageIndex: msgIndex  // Store message index for sorting within same conversation
              });
            }
          }
        });
      }
    });
    
    // Sort by message timestamp (newest first)
    return allMessages.sort((a, b) => {
      const getTimestamp = (msg) => {
        // First try the message's timestamp field (full ISO timestamp)
        if (msg.timestamp) {
          const ts = new Date(msg.timestamp).getTime();
          if (!isNaN(ts)) return ts;
        }
        
        // IMPORTANT: Try the message ID which contains Unix timestamp (most reliable for individual messages)
        if (msg.id && typeof msg.id === 'string' && msg.id.includes('msg-')) {
          const parts = msg.id.split('-');
          const timestampStr = parts[parts.length - 1];
          const timestamp = parseFloat(timestampStr);
          
          if (!isNaN(timestamp) && timestamp > 0) {
            // Message IDs store Unix timestamp which can be in seconds or milliseconds
            // If timestamp is less than year 2000 in seconds (946684800), it's probably in seconds
            // Most modern timestamps are > 1600000000 (Sept 2020)
            const tsMillis = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
            return tsMillis;
          }
        }
        
        // Try conversation's updated_at field (least specific - all messages in conv have same time)
        if (msg.conversation && msg.conversation.updated_at) {
          const ts = new Date(msg.conversation.updated_at).getTime();
          if (!isNaN(ts)) return ts;
        }
        
        // Fallback to conversation timestamp
        if (msg.conversationTimestamp) {
          const ts = new Date(msg.conversationTimestamp).getTime();
          if (!isNaN(ts)) return ts;
        }
        
        // Last resort - return 0
        return 0;
      };
      
      const timeA = getTimestamp(a);
      const timeB = getTimestamp(b);
      
      // Sort newest first (descending)
      return timeB - timeA;
    });
  })() : [];

  // Filter conversations (for 'all' folder and contact category folders)
  // Include Mac bridge conversations in the list when on 'all' tab
  const allConversations = activeTab === 'all' 
    ? [...conversations, ...macBridgeConversations]
    : activeTab === 'mac' 
      ? macBridgeConversations 
      : conversations;
      
  const filteredConversations = allConversations
    .filter(conv => {

      // Filter by folder (left sidebar - uses messageCategory for keyword-based filtering)
      let folderMatch = true;
      if (selectedFolder !== 'all') {
        // Check if filtering by message category (keyword-based)
        if (['meeting_request', 'introduction', 'urgent', 'opportunity', 'travel', 'general'].includes(selectedFolder)) {
          // Check if conversation has ANY message with this category (using categories array)
          const categories = conv.categories || [conv.messageCategory];
          folderMatch = categories.includes(selectedFolder);
        }
        // Check if filtering by contact category (business category)
        else if (['cresa', 'software', 'personal', 'speaking', 'investment'].includes(selectedFolder)) {
          folderMatch = conv.contactCategory === selectedFolder;
        }
      }
      
      // Filter by type
      let typeMatch = true;
      if (activeTab === 'texts') typeMatch = conv.type === 'text';
      if (activeTab === 'emails') typeMatch = conv.type === 'email';
      if (activeTab === 'personal') typeMatch = conv.type === 'personal_phone';
      if (activeTab === 'mac') typeMatch = conv.type === 'mac_bridge';
      
      // Filter by search query
      const searchMatch = searchQuery.trim() === '' || 
        conv.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase());
      
      return folderMatch && typeMatch && searchMatch;
    })
    .sort((a, b) => {
      // Sort by most recent first (newest at top)
      // Use updated_at if available, fallback to timestamp, then to last message time
      const getTimestamp = (conv) => {
        // Try to get the most recent message timestamp from the messages array
        if (conv.messages && conv.messages.length > 0) {
          const lastMsg = conv.messages[conv.messages.length - 1];
          if (lastMsg.timestamp) {
            const ts = new Date(lastMsg.timestamp).getTime();
            if (!isNaN(ts)) return ts;
          }
        }
        
        // Try updated_at field
        if (conv.updated_at) {
          const ts = new Date(conv.updated_at).getTime();
          if (!isNaN(ts)) return ts;
        }
        
        // Try timestamp field
        if (conv.timestamp) {
          const ts = new Date(conv.timestamp).getTime();
          if (!isNaN(ts)) return ts;
        }
        
        // Last resort - use creation time or return 0
        return 0;
      };
      
      const timeA = getTimestamp(a);
      const timeB = getTimestamp(b);
      
      // Sort descending (newest first)
      return timeB - timeA;
    });

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className="text-white">Loading conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-red-400 font-semibold mb-1">Error Loading Messages</p>
              <p className="text-red-300 text-sm">{errorMessage}</p>
              <button 
                onClick={() => {
                  setErrorMessage(null);
                  fetchConversations();
                }}
                className="mt-2 px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Text Center</h1>
          <p className="text-white">Send and receive text messages in one place</p>
          <div className="flex flex-col gap-1">
            {pushcutSecret && (
              <p className="text-green-400 text-sm flex items-center gap-2">
                <Smartphone size={14} />
                iPhone SMS enabled via Pushcut
              </p>
            )}
            {macMessagesEnabled && (
              <p className="text-purple-400 text-sm flex items-center gap-2">
                <Monitor size={14} />
                Mac Messages enabled (auto-send)
              </p>
            )}
          </div>
        </div>
        
        <div className="flex gap-3">
          {/* Sync Button */}
          <Button 
            onClick={syncOutlookEmails}
            disabled={isSyncingEmails}
            className="bg-white hover:bg-gray-100 text-black"
          >
            <RefreshCw size={18} className={`mr-2 ${isSyncingEmails ? 'animate-spin' : ''}`} />
            {isSyncingEmails ? 'Syncing...' : 'Sync'}
          </Button>

          {/* Mac Messages Toggle */}
          <Button 
            onClick={() => {
              const newState = !macMessagesEnabled;
              setMacMessagesEnabled(newState);
              localStorage.setItem('mac_messages_enabled', newState);
              if (newState) {
                alert('✅ Mac Messages enabled!\n\nMessages will be sent automatically through your Mac.\n\nMake sure:\n1. Backend is running on a Mac\n2. Messages app is signed in');
              }
            }}
            className={`${macMessagesEnabled ? 'bg-purple-600 hover:bg-purple-700' : 'bg-black hover:bg-white/5'} text-white`}
          >
            <Monitor size={18} className="mr-2" />
            {macMessagesEnabled ? 'Mac SMS: ON' : 'Enable Mac SMS'}
          </Button>
          
          {/* Pushcut Setup Link */}
          {!pushcutSecret && (
            <Button 
              onClick={() => window.location.href = '/pushcut-test'}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Smartphone size={18} className="mr-2" />
              Setup iPhone SMS
            </Button>
          )}
          
          {/* Create Message Button */}
          <Dialog open={isComposeOpen} onOpenChange={setIsComposeOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white hover:bg-gray-100 text-black">
                <Plus size={18} className="mr-2" />
                Create Message
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-black border-white/60 text-white max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-white">New Message</DialogTitle>
              </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Manual Entry Toggle */}
              <div className="flex items-center justify-between p-3 bg-transparent rounded-lg">
                <span className="text-sm text-white">Enter phone/email manually</span>
                <button
                  type="button"
                  onClick={() => setUseManualEntry(!useManualEntry)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    useManualEntry ? 'bg-white' : 'bg-transparent'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      useManualEntry ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Contact Selector OR Manual Entry */}
              {!useManualEntry ? (
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Select Contact
                  </label>
                  <select
                    value={selectedContact}
                    onChange={(e) => setSelectedContact(e.target.value)}
                    className="w-full backdrop-blur-sm bg-transparent border border-white/60 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white"
                  >
                    <option value="">Choose a contact...</option>
                    {contacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.name} {contact.phone ? `(${contact.phone})` : contact.email ? `(${contact.email})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      placeholder="Recipient name"
                      className="w-full backdrop-blur-sm bg-transparent border border-white/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      {messageType === 'sms' ? 'Phone Number' : 'Email Address'}
                    </label>
                    {messageType === 'sms' ? (
                      <input
                        type="tel"
                        value={manualPhone}
                        onChange={(e) => setManualPhone(e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        className="w-full backdrop-blur-sm bg-transparent border border-white/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                      />
                    ) : (
                      <input
                        type="email"
                        value={manualEmail}
                        onChange={(e) => setManualEmail(e.target.value)}
                        placeholder="email@example.com"
                        className="w-full backdrop-blur-sm bg-transparent border border-white/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Message Type */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Message Type
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setMessageType('sms')}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                      messageType === 'sms' 
                        ? 'bg-white text-black' 
                        : 'bg-black text-white hover:bg-white/5'
                    }`}
                  >
                    <MessageSquare size={16} className="inline mr-2" />
                    SMS Text
                  </button>
                  <button
                    onClick={() => setMessageType('email')}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                      messageType === 'email' 
                        ? 'bg-white text-black' 
                        : 'bg-black text-white hover:bg-white/5'
                    }`}
                  >
                    <Mail size={16} className="inline mr-2" />
                    Email
                  </button>
                </div>
              </div>

              {/* Subject (Email only) */}
              {messageType === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={composeSubject}
                    onChange={(e) => setComposeSubject(e.target.value)}
                    placeholder="Enter subject..."
                    className="w-full backdrop-blur-sm bg-transparent border border-white/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  />
                </div>
              )}

              {/* Message Body */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Message
                </label>
                <textarea
                  value={composeMessage}
                  onChange={(e) => setComposeMessage(e.target.value)}
                  placeholder={messageType === 'sms' ? 'Type your text message...' : 'Type your email...'}
                  rows={6}
                  className="w-full backdrop-blur-sm bg-transparent border border-white/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white resize-none"
                />
                {messageType === 'sms' && (
                  <p className="text-xs text-white mt-1">
                    {composeMessage.length} characters
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  onClick={() => setIsComposeOpen(false)}
                  variant="outline"
                  className="border-white/60 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (!selectedContact || !composeMessage.trim()) {
                      alert('Please select a contact and enter a message');
                      return;
                    }
                    // Pre-fill schedule dialog with compose message data
                    setMessageToSchedule(composeMessage);
                    setIsDraftSchedule(false);
                    
                    // Set default schedule time to 1 hour from now
                    const now = new Date();
                    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
                    const localDateTime = oneHourLater.toISOString().slice(0, 16);
                    setScheduleDateTime(localDateTime);
                    
                    setIsScheduleDialogOpen(true);
                  }}
                  disabled={!selectedContact || !composeMessage.trim()}
                  className="bg-black hover:bg-white/5 text-white"
                >
                  <Clock size={16} className="mr-2" />
                  Schedule
                </Button>
                {messageType === 'sms' && macMessagesEnabled && (
                  <Button
                    onClick={handleSendViaMacCompose}
                    disabled={sending || !selectedContact || !composeMessage.trim()}
                    className="bg-gradient-to-r from-purple-600 to-gray-600 hover:from-purple-700 hover:to-gray-700 text-white"
                  >
                    {sending ? 'Sending...' : (
                      <>
                        <Monitor size={16} className="mr-2" />
                        Send via Mac
                      </>
                    )}
                  </Button>
                )}
                {messageType === 'sms' && pushcutSecret && (
                  <Button
                    onClick={handleSendViaPushcutCompose}
                    disabled={sending || !selectedContact || !composeMessage.trim()}
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white"
                  >
                    {sending ? 'Sending...' : (
                      <>
                        <Smartphone size={16} className="mr-2" />
                        Send via iPhone
                      </>
                    )}
                  </Button>
                )}
                <Button
                  onClick={handleSendNewMessage}
                  disabled={sending || !selectedContact || !composeMessage.trim()}
                  className="bg-white hover:bg-cyan-600 text-white"
                >
                  {sending ? 'Sending...' : (
                    <>
                      <Send size={16} className="mr-2" />
                      Send {messageType === 'sms' ? 'Text' : 'Email'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
        {/* Folders Sidebar - Hidden on mobile, shown on large screens */}
        <div className="hidden lg:block lg:col-span-2 bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-4 overflow-y-auto" style={{ height: '700px' }}>
          <h3 className="text-sm font-semibold text-white mb-4">Keyword Folders</h3>
          <div className="space-y-1">
            {folders.map((folder) => (
              <button
                key={folder.id}
                onClick={() => setSelectedFolder(folder.id)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedFolder === folder.id
                    ? 'bg-white text-black'
                    : 'text-white hover:text-white hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded ${folder.color}`}></span>
                  <span>{folder.name}</span>
                </div>
                {folder.count > 0 && (
                  <span className={`text-xs font-semibold ${
                    selectedFolder === folder.id ? 'text-white' : 'text-white'
                  }`}>
                    {folder.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Conversations List - Full width on mobile, hidden when conversation selected */}
        <div className={`col-span-1 lg:col-span-4 bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 ${selectedConversation ? 'hidden lg:block' : 'block'}`} style={{ height: '700px' }}>
          <div className="p-4 border-b border-white/60">
            {/* Mobile Folder Selector */}
            <div className="lg:hidden mb-4">
              <select
                value={selectedFolder}
                onChange={(e) => setSelectedFolder(e.target.value)}
                className="w-full backdrop-blur-sm bg-transparent border border-white/60 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white"
              >
                <optgroup label="📁 Folders">
                  <option value="all">All Messages</option>
                  <option value="inbox">📥 Inbox</option>
                  <option value="sent">📤 Sent</option>
                  <option value="drafts">📝 Drafts</option>
                  <option value="scheduled">⏰ Scheduled</option>
                  <option value="trash">🗑️ Trash</option>
                </optgroup>
                <optgroup label="🏷️ Keyword Folders">
                  <option value="meeting_request">📅 Meeting Request</option>
                  <option value="introduction">👋 Introduction</option>
                  <option value="urgent">⚡ Urgent</option>
                  <option value="opportunity">💼 Opportunity</option>
                  <option value="general">💬 General</option>
                </optgroup>
                <optgroup label="📂 Categories">
                  <option value="cresa">🏢 Cresa</option>
                  <option value="software">💻 Software</option>
                  <option value="personal">👤 Personal</option>
                  <option value="speaking">🎤 Speaking</option>
                  <option value="investment">📊 Investment</option>
                </optgroup>
              </select>
            </div>
            
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white z-10" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="w-full bg-transparent border border-white/60 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-white focus:outline-none focus:border-white/60"
              />
            </div>
            
            {/* Type Filter Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('all')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'all' ? 'bg-white text-black' : 'bg-black text-white hover:text-white'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveTab('personal')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'personal' ? 'bg-green-500 text-white' : 'bg-black text-white hover:text-white'
                }`}
              >
                <Phone size={16} className="inline mr-1" />
                Personal
              </button>
              <button
                onClick={() => setActiveTab('texts')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'texts' ? 'bg-white text-black' : 'bg-black text-white hover:text-white'
                }`}
              >
                <MessageSquare size={16} className="inline mr-1" />
                Business
              </button>
              <button
                onClick={() => setActiveTab('mac')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'mac' ? 'bg-purple-500 text-white' : 'bg-black text-white hover:text-white'
                }`}
              >
                <Monitor size={16} className="inline mr-1" />
                Mac
                {macBridgeStatus.connected && <span className="ml-1 w-2 h-2 bg-green-400 rounded-full inline-block"></span>}
              </button>
            </div>
          </div>

          <div className="divide-y divide-gray-800 overflow-y-auto" style={{ height: 'calc(700px - 230px)' }}>
            {/* Display Individual Messages (when keyword folder is selected) */}
            {isViewingMessages && filteredMessages.map((msg) => (
              <div
                key={msg.id}
                className={`group p-4 cursor-pointer transition-colors relative hover:bg-transparent`}
                onClick={() => handleSelectConversation(msg.conversation)}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-500 to-purple-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                    {msg.conversationName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-white font-semibold text-sm">
                          {msg.conversationName}
                        </h4>
                        {/* Phone Type Badge */}
                        {msg.conversationType === 'personal_phone' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500/20 text-green-400 border border-green-500/30">
                            Personal
                          </span>
                        )}
                        {msg.conversationType === 'text' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-cyan-400 border border-cyan-500/30">
                            Business
                          </span>
                        )}
                        {/* Message Category Badge */}
                        {msg.category && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getCategoryBadgeStyle(msg.category)}`}>
                            {getCategoryDisplayName(msg.category)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <MessageSquare size={14} className="text-white" />
                      </div>
                    </div>
                    <p className="text-sm text-white mb-1">{msg.text}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white">{msg.sender}</span>
                      <span className="text-xs text-gray-600">•</span>
                      <span className="text-xs text-white">{formatTimestamp(msg.timestamp || msg.conversationTimestamp || msg.time)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Display SMS and Personal Phone Conversations (when 'all' folder or contact category selected) */}
            {!isViewingMessages && (activeTab === 'all' || activeTab === 'texts' || activeTab === 'personal' || activeTab === 'mac') && filteredConversations.map((conv) => (
              <div
                key={conv.id}
                className={`group p-4 cursor-pointer transition-colors relative ${
                  selectedConversation?.id === conv.id ? 'bg-transparent' : 'hover:bg-transparent'
                }`}
              >
                <div onClick={() => handleSelectConversation(conv)} className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-500 to-purple-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 relative">
                    {conv.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??'}
                    {/* Group indicator badge */}
                    {conv.is_group && (
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-r bg-white rounded-full flex items-center justify-center border-2 border-gray-900">
                        <Users size={12} className="text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-white font-semibold text-sm truncate">
                          {conv.group_name || conv.name}
                        </h4>
                        {/* Group member count */}
                        {conv.is_group && conv.participants && conv.participants.length > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white border border-white/30">
                            {conv.participants.length} members
                          </span>
                        )}
                        {/* Phone Type Badge (Personal or Business) */}
                        {conv.type === 'personal_phone' && !conv.is_group && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500/20 text-green-400 border border-green-500/30">
                            Personal
                          </span>
                        )}
                        {conv.type === 'text' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-cyan-400 border border-cyan-500/30">
                            Business
                          </span>
                        )}
                        {conv.type === 'mac_bridge' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400 border border-purple-500/30">
                            Mac
                          </span>
                        )}
                        {/* Message Category Badge (Keyword-based: Meeting, Intro, Urgent, etc.) */}
                        {conv.messageCategory && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getCategoryBadgeStyle(conv.messageCategory)}`}>
                            {getCategoryDisplayName(conv.messageCategory)}
                          </span>
                        )}
                        {/* Contact Category Badge (Business: Cresa, Personal, etc.) */}
                        {conv.contactCategory && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getCategoryBadgeStyle(conv.contactCategory)}`}>
                            {getCategoryDisplayName(conv.contactCategory)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mr-10">
                        {conv.unread > 0 && (
                          <span className="w-5 h-5 bg-white rounded-full text-black text-xs flex items-center justify-center font-bold">
                            {conv.unread}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Show participant names for group chats */}
                    {conv.is_group && conv.participant_names && conv.participant_names.length > 0 && (
                      <p className="text-xs text-gray-400 truncate mb-1">
                        {conv.participant_names.slice(0, 3).join(', ')}
                        {conv.participant_names.length > 3 && ` +${conv.participant_names.length - 3}`}
                      </p>
                    )}
                    <p className="text-xs text-white truncate mb-1">{conv.lastMessage}</p>
                    <span className="text-xs text-white">{formatTimestamp(conv.updated_at || conv.timestamp)}</span>
                  </div>
                </div>
                {/* Delete Button */}
                <button
                  onClick={(e) => handleDeleteConversation(conv.id, e)}
                  className="absolute top-4 right-4 p-2 text-white hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                  title="Delete conversation"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            
            {/* Empty State for Conversations */}
            {!loading && filteredConversations.length === 0 && (activeTab === 'all' || activeTab === 'texts' || activeTab === 'personal') && (
              <div className="p-8 text-center">
                <MessageSquare size={48} className="mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400 text-lg mb-2">No conversations found</p>
                <p className="text-gray-500 text-sm">
                  {searchQuery ? 'Try adjusting your search query' : 
                   selectedFolder !== 'all' ? 'No messages in this category' : 
                   'Your text messages will appear here'}
                </p>
                <button
                  onClick={() => {
                    console.log('🔄 Manual refresh triggered');
                    console.log('Current conversations count:', conversations.length);
                    console.log('Filtered conversations count:', filteredConversations.length);
                    fetchConversations();
                  }}
                  className="mt-4 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
                >
                  Refresh Conversations
                </button>
              </div>
            )}

            {/* Mac Messages Bridge Section */}
            {activeTab === 'mac' && (
              <>
                {/* Bridge Status */}
                <div className="p-4 border-b border-gray-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Monitor size={20} className={macBridgeStatus.connected ? 'text-green-400' : 'text-gray-500'} />
                      <span className="text-white font-medium">
                        {macBridgeStatus.connected ? `Connected: ${macBridgeStatus.mac_name || 'Mac'}` : 'Mac Bridge Offline'}
                      </span>
                    </div>
                    {macBridgeStatus.connected && macBridgeStatus.last_sync && (
                      <span className="text-xs text-gray-400">
                        Last sync: {new Date(macBridgeStatus.last_sync).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                  {!macBridgeStatus.connected && (
                    <p className="text-sm text-gray-400 mt-2">
                      Run the Mac Messages Bridge script on your Mac to sync iMessages.
                      <a href="/MAC_MESSAGES_BRIDGE_GUIDE.md" target="_blank" className="text-cyan-400 ml-1 hover:underline">
                        Setup Guide
                      </a>
                    </p>
                  )}
                </div>

                {/* Empty state for Mac Bridge */}
                {macBridgeStatus.connected && macBridgeConversations.length === 0 && (
                  <div className="p-8 text-center">
                    <Monitor size={48} className="mx-auto mb-4 text-gray-600" />
                    <p className="text-gray-400 text-lg mb-2">No Mac Messages synced</p>
                    <p className="text-gray-500 text-sm">
                      Messages from your Mac will appear here once synced
                    </p>
                  </div>
                )}

                {/* Loading state */}
                {loadingMacBridge && (
                  <div className="p-4 text-center text-gray-400">Loading messages...</div>
                )}
              </>
            )}
            
            {/* Display Outlook Emails */}
            {(activeTab === 'all' || activeTab === 'emails') && outlookConnected && emails.map((email) => (
              <div
                key={email.id}
                className="group p-4 cursor-pointer transition-colors relative hover:bg-transparent"
                onClick={() => handleSelectConversation({
                  id: email.id,
                  name: email.from_name || email.from,
                  type: 'email',
                  avatar: (email.from_name || email.from).substring(0, 2).toUpperCase(),
                  lastMessage: email.subject,
                  timestamp: new Date(email.receivedDateTime).toLocaleDateString(),
                  unread: email.isRead ? 0 : 1,
                  email: email
                })}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-500 to-gray-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                    {(email.from_name || email.from).substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-white font-semibold text-sm truncate">{email.from_name || email.from}</h4>
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-blue-500" />
                        {!email.isRead && (
                          <span className="w-2 h-2 bg-white rounded-full"></span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs font-semibold text-white mb-1 truncate">{email.subject}</p>
                    <p className="text-xs text-white truncate">{email.bodyPreview}</p>
                    <span className="text-xs text-white">{new Date(email.receivedDateTime).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Loading state */}
            {loadingEmails && activeTab === 'emails' && (
              <div className="p-4 text-center text-white">Loading emails...</div>
            )}
            
            {/* No emails state */}
            {!loadingEmails && outlookConnected && activeTab === 'emails' && emails.length === 0 && (
              <div className="p-4 text-center text-white">No emails found</div>
            )}

          </div>
        </div>

        {/* Message View */}
        {/* Active Conversation - Full width on mobile, shown only when conversation selected */}
        <div className={`col-span-1 lg:col-span-6 bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 flex flex-col ${selectedConversation ? 'block' : 'hidden lg:block'}`} style={{ height: '700px' }}>
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 lg:p-6 border-b border-white/60 flex items-center justify-between">
                {/* Back Button for Mobile */}
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="lg:hidden mr-3 p-2 text-white hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
                
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-500 to-purple-500 flex items-center justify-center text-white font-semibold relative">
                    {selectedConversation.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    {/* Group indicator badge */}
                    {selectedConversation.is_group && (
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-r bg-white rounded-full flex items-center justify-center border-2 border-gray-900">
                        <Users size={12} className="text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isEditingGroupName && selectedConversation.is_group ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editedGroupName}
                            onChange={(e) => setEditedGroupName(e.target.value)}
                            className="px-2 py-1 bg-transparent border border-white/60 rounded text-white text-sm"
                            placeholder="Group name"
                            autoFocus
                          />
                          <button
                            onClick={handleUpdateGroupName}
                            className="text-green-400 hover:text-green-300"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingGroupName(false);
                              setEditedGroupName('');
                            }}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <h3 className="text-lg font-bold text-white hover:text-cyan-500 cursor-pointer transition-colors" onClick={() => selectedConversation.contact_id && handleEditContact(selectedConversation)}>
                            {selectedConversation.group_name || selectedConversation.name}
                          </h3>
                          {selectedConversation.is_group && (
                            <button
                              onClick={() => {
                                setIsEditingGroupName(true);
                                setEditedGroupName(selectedConversation.group_name || selectedConversation.name);
                              }}
                              className="text-gray-400 hover:text-white transition-colors"
                              title="Edit group name"
                            >
                              <Edit size={14} />
                            </button>
                          )}
                        </>
                      )}
                      {/* Message Category Badge (Keyword-based) */}
                      {selectedConversation.messageCategory && (
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getCategoryBadgeStyle(selectedConversation.messageCategory)}`}>
                          {getCategoryDisplayName(selectedConversation.messageCategory)}
                        </span>
                      )}
                      {/* Contact Category Badge (Business category) */}
                      {selectedConversation.contactCategory && (
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getCategoryBadgeStyle(selectedConversation.contactCategory)}`}>
                          {getCategoryDisplayName(selectedConversation.contactCategory)}
                        </span>
                      )}
                      {selectedConversation.contact_id ? (
                        <Edit size={16} className="text-white hover:text-cyan-500 cursor-pointer" onClick={() => handleEditContact(selectedConversation)} />
                      ) : (
                        <Button
                          onClick={() => handleAddContactFromConversation(selectedConversation)}
                          size="sm"
                          className="bg-white hover:bg-gray-100 text-black text-xs px-2 py-1 h-6"
                        >
                          <Plus size={12} className="mr-1" />
                          Add Contact
                        </Button>
                      )}
                      {/* Call Button */}
                      <Button
                        onClick={() => {
                          const phone = selectedConversation?.phone || selectedConversation?.email?.sender;
                          const name = selectedConversation?.name || selectedConversation?.contact_name || 'Contact';
                          handleMakeCall(phone, name);
                        }}
                        disabled={isCallingInProgress}
                        size="sm"
                        className={`bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 h-6 ${isCallingInProgress ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={`Call ${selectedConversation?.name || 'contact'}`}
                      >
                        <Phone size={12} className="mr-1" />
                        Call
                      </Button>
                    </div>
                    <p className="text-sm text-white flex items-center gap-2">
                      {selectedConversation.type === 'email' || selectedConversation.type === 'outlook_email' ? (
                        <><Mail size={14} /> Email Conversation</>
                      ) : selectedConversation.type === 'mac_bridge' ? (
                        <><Monitor size={14} className="text-purple-400" /> Mac iMessage</>
                      ) : selectedConversation.is_group ? (
                        <>
                          <Users size={14} /> Group Chat
                          {selectedConversation.participants && selectedConversation.participants.length > 0 && (
                            <span className="text-gray-400">• {selectedConversation.participants.length} members</span>
                          )}
                        </>
                      ) : (
                        <><MessageSquare size={14} /> Text Messages</>
                      )}
                    </p>
                    {/* Show participant names for group chats */}
                    {selectedConversation.is_group && selectedConversation.participant_names && selectedConversation.participant_names.length > 0 && (
                      <p className="text-xs text-gray-400 mt-1">
                        {selectedConversation.participant_names.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 p-6 overflow-y-auto" style={{ maxHeight: '460px' }}>
                {selectedConversation.type === 'outlook_email' ? (
                  /* Outlook Email View */
                  <div className="bg-black rounded-lg p-6">
                    <div className="mb-4 pb-4 border-b border-white/60">
                      <h2 className="text-xl font-bold text-white mb-2">
                        {selectedConversation.email?.subject || '(No Subject)'}
                      </h2>
                      <div className="flex items-center gap-3 text-sm text-white">
                        <span className="font-medium text-white">
                          {selectedConversation.email?.sender_name || selectedConversation.email?.sender}
                        </span>
                        <span>&lt;{selectedConversation.email?.sender}&gt;</span>
                        <span>•</span>
                        <span>{new Date(selectedConversation.email?.received_datetime).toLocaleString()}</span>
                      </div>
                      {selectedConversation.email?.recipients?.length > 0 && (
                        <div className="mt-2 text-sm text-white">
                          <span className="font-medium">To:</span> {selectedConversation.email.recipients.join(', ')}
                        </div>
                      )}
                      {selectedConversation.email?.cc?.length > 0 && (
                        <div className="mt-1 text-sm text-white">
                          <span className="font-medium">Cc:</span> {selectedConversation.email.cc.join(', ')}
                        </div>
                      )}
                    </div>
                    <div 
                      className="prose prose-invert max-w-none text-white"
                      dangerouslySetInnerHTML={{ __html: selectedConversation.email?.body || 'No content' }}
                    />
                  </div>
                ) : (
                  /* Regular SMS/Conversation View */
                  <div className="space-y-4">
                    {(() => {
                      // Combine regular messages with scheduled messages
                      const regularMsgs = selectedConversation.messages || [];
                      const scheduledMsgs = conversationScheduledMsgs.map(sm => ({
                        id: sm.id,
                        text: sm.message_text,
                        time: `Scheduled: ${new Date(sm.scheduled_time).toLocaleString()}`,
                        sent: true,
                        scheduled: true,
                        scheduledData: sm,
                        timestamp: new Date(sm.scheduled_time).getTime()
                      }));
                      
                      // Sort all messages by time
                      const allMessages = [...regularMsgs, ...scheduledMsgs].sort((a, b) => {
                        const timeA = a.timestamp || new Date(a.time).getTime();
                        const timeB = b.timestamp || new Date(b.time).getTime();
                        return timeA - timeB;
                      });
                      
                      return allMessages.map((message) => {
                      // Get category color for incoming messages
                      const getCategoryColor = (category) => {
                        const colors = {
                          'meeting_request': 'border-l-4 border-blue-500',
                          'introduction': 'border-l-4 border-green-500',
                          'urgent': 'border-l-4 border-red-500',
                          'opportunity': 'border-l-4 border-purple-500',
                          'general': 'border-l-4 border-white/60'
                        };
                        return colors[category] || '';
                      };

                      return (
                        <div key={message.id} className={`flex ${message.is_from_me ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-md ${message.is_from_me ? 'order-2' : 'order-1'} group relative`}>
                            <div className={`rounded-lg p-4 ${
                              message.scheduled
                                ? 'bg-yellow-500/30 border-2 border-yellow-500 text-white'
                                : message.is_from_me 
                                ? 'bg-blue-600 text-white'
                                : `bg-gray-700 text-white ${message.category ? getCategoryColor(message.category) : ''}`
                            }`}>
                              {/* Show scheduled badge */}
                              {message.scheduled && (
                                <div className="flex items-center justify-between mb-2">
                                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-500 text-black flex items-center gap-1">
                                    <Clock size={12} />
                                    Scheduled
                                  </span>
                                  <button
                                    onClick={() => {
                                      setEditingScheduledMsg(message.scheduledData);
                                      setEditScheduleText(message.scheduledData.message_text);
                                      setEditScheduleTime(new Date(message.scheduledData.scheduled_time).toISOString().slice(0, 16));
                                      setIsEditScheduleOpen(true);
                                    }}
                                    className="p-1 hover:bg-yellow-500/50 rounded transition-colors"
                                    title="Edit scheduled message"
                                  >
                                    <Edit size={14} />
                                  </button>
                                </div>
                              )}
                              {/* Show category badge for incoming messages with keywords */}
                              {!message.is_from_me && !message.scheduled && message.category && message.category !== 'general' && (
                                <div className="mb-2">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getCategoryBadgeStyle(message.category)}`}>
                                    {getCategoryDisplayName(message.category)}
                                  </span>
                                </div>
                              )}
                              <p className="text-sm">{message.text}</p>
                              
                              {/* Display image if media_url exists */}
                              {message.media_url && message.media_type === 'image' && (
                                <div className="mt-2">
                                  <img 
                                    src={message.media_url} 
                                    alt="Message attachment" 
                                    className="rounded-lg max-w-full max-h-60 object-cover cursor-pointer"
                                    onClick={() => window.open(message.media_url, '_blank')}
                                  />
                                </div>
                              )}
                              
                              {/* Display attachments from Mac Messages */}
                              {message.attachments && message.attachments.length > 0 && (
                                <div className="mt-2 space-y-2">
                                  {message.attachments.map((att, idx) => (
                                    <div key={idx}>
                                      {att.type === 'image' ? (
                                        att.missing ? (
                                          <div className="bg-gray-700 rounded-lg p-3 text-sm text-gray-400 flex items-center gap-2">
                                            <span>📷</span>
                                            <span>Image: {att.filename || 'Unknown'}</span>
                                            <span className="text-xs">(not synced)</span>
                                          </div>
                                        ) : att.url ? (
                                          <div className="rounded-lg overflow-hidden max-w-xs">
                                            <img 
                                              src={`${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                                              alt={att.filename || 'Image attachment'}
                                              className="w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${att.url}`, '_blank')}
                                            />
                                            <p className="text-xs text-gray-400 mt-1">{att.filename}</p>
                                          </div>
                                        ) : (
                                          <div className="bg-gray-700/50 rounded-lg p-2 text-sm text-gray-300 flex items-center gap-2">
                                            <span>📷</span>
                                            <span>{att.filename || 'Image'}</span>
                                          </div>
                                        )
                                      ) : (
                                        <div className="bg-gray-700 rounded-lg p-2 text-sm text-gray-400 flex items-center gap-2">
                                          <span>📎</span>
                                          <span>{att.filename || 'Attachment'}</span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Reaction display and add button */}
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {message.reaction && (
                                  <span className="text-lg">{message.reaction === 'heart' ? '❤️' : message.reaction === 'like' ? '👍' : message.reaction}</span>
                                )}
                                {!message.scheduled && (
                                  <button
                                    onClick={async () => {
                                      const newReaction = message.reaction ? null : 'heart';
                                      try {
                                        await conversationsAPI.addReaction(selectedConversation.id, message.id, newReaction || '');
                                        // Update local state
                                        const updatedConv = {
                                          ...selectedConversation,
                                          messages: selectedConversation.messages.map(m => 
                                            m.id === message.id ? {...m, reaction: newReaction} : m
                                          )
                                        };
                                        setSelectedConversation(updatedConv);
                                      } catch (error) {
                                        console.error('Failed to add reaction:', error);
                                      }
                                    }}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-sm hover:scale-110"
                                    title={message.reaction ? "Remove reaction" : "Add heart"}
                                  >
                                    {message.reaction ? '💔' : '❤️'}
                                  </button>
                                )}
                                
                                {/* Add to Meeting Button - for incoming messages */}
                                {!message.sent && (
                                  <>
                                    <button
                                      onClick={() => {
                                        // Skip AI extraction, just use default data for instant navigation
                                        const today = new Date();
                                        const meetingData = {
                                          title: `Meeting - ${selectedConversation.name}`,
                                          date: today.toISOString().split('T')[0],
                                          time: '09:00',
                                          duration: 60,
                                          location: '',
                                          notes: `From text message:\n\n${message.text}`
                                        };
                                        
                                        // Navigate immediately to meetings page
                                        window.location.href = `/meetings?prefill=${encodeURIComponent(JSON.stringify(meetingData))}`;
                                      }}
                                      className="opacity-0 group-hover:opacity-100 px-2 py-1 text-white text-xs rounded transition-all flex items-center gap-1 bg-purple-500/30 hover:bg-purple-500/50"
                                      title="Create new meeting from this message"
                                    >
                                      <Calendar size={12} />
                                      New Meeting
                                    </button>
                                    
                                    <button
                                      onClick={async () => {
                                        try {
                                          // Load existing meetings from correct endpoint
                                          const allMeetings = await apiCall('/meetings/');
                                          // Filter for upcoming meetings only
                                          const today = new Date().toISOString().split('T')[0];
                                          const upcomingMeetings = allMeetings.filter(m => m.date >= today);
                                          setExistingMeetings(upcomingMeetings);
                                          setSelectedMessageForMeeting(message);
                                          setShowMeetingSelector(true);
                                        } catch (error) {
                                          console.error('Error loading meetings:', error);
                                          alert('Failed to load meetings');
                                        }
                                      }}
                                      className="opacity-0 group-hover:opacity-100 px-2 py-1 bg-white/30 hover:bg-white/50 text-white text-xs rounded transition-all flex items-center gap-1"
                                      title="Add this message to an existing meeting"
                                    >
                                      <Plus size={12} />
                                      Add to Meeting
                                    </button>
                                  </>
                                )}
                              </div>
                              
                              {/* Edit/Delete buttons for sent messages */}
                              {message.is_from_me && !message.scheduled && (
                                <div className="flex gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => {
                                      const newText = prompt('Edit message:', message.text);
                                      if (newText && newText !== message.text) {
                                        // Update message locally (can't edit via Twilio API)
                                        const updatedConv = {
                                          ...selectedConversation,
                                          messages: selectedConversation.messages.map(m => 
                                            m.id === message.id ? {...m, text: newText} : m
                                          )
                                        };
                                        setSelectedConversation(updatedConv);
                                        alert('✅ Message updated locally (note: SMS cannot be edited on recipient side)');
                                      }
                                    }}
                                    className="px-2 py-1 bg-white hover:bg-gray-100 rounded text-xs flex items-center gap-1 text-black font-medium"
                                    title="Edit message (local only)"
                                  >
                                    <Edit size={12} className="text-black" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (window.confirm('Delete this message from conversation?')) {
                                        const updatedConv = {
                                          ...selectedConversation,
                                          messages: selectedConversation.messages.filter(m => m.id !== message.id)
                                        };
                                        setSelectedConversation(updatedConv);
                                        alert('✅ Message deleted locally');
                                      }
                                    }}
                                    className="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs flex items-center gap-1 text-white font-medium"
                                    title="Delete message"
                                  >
                                    <Trash2 size={12} className="text-white" />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-white mt-1 px-2">{message.time}</p>
                          </div>
                        </div>
                      );
                    });
                    })()}
                    {/* Scroll target for auto-scroll to bottom */}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-white/60">
                {/* Email form removed */}
                <div className="flex items-center gap-3 relative">
                  <button 
                    onClick={() => setShowLinkDialog(!showLinkDialog)}
                    className="p-2 text-white hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    title="Insert link"
                  >
                    <LinkIcon size={20} />
                  </button>
                  <button 
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-2 text-white hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    title="Insert emoji"
                  >
                    <Smile size={20} />
                  </button>
                  
                  {/* Emoji Picker */}
                  {showEmojiPicker && (
                    <div className="absolute bottom-16 left-0 backdrop-blur-sm bg-transparent border border-white/60 rounded-lg p-3 shadow-xl z-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-white font-medium">Select Emoji</span>
                        <button onClick={() => setShowEmojiPicker(false)} className="text-white hover:text-white">
                          <X size={16} />
                        </button>
                      </div>
                      <div className="grid grid-cols-10 gap-2 max-w-md">
                        {emojis.map((emoji, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleEmojiSelect(emoji)}
                            className="text-2xl hover:bg-white/5 rounded p-1 transition-colors"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Link Dialog */}
                  {showLinkDialog && (
                    <div className="absolute bottom-16 left-0 backdrop-blur-sm bg-transparent border border-white/60 rounded-lg p-4 shadow-xl z-50 w-96">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-white font-medium">Insert Link</span>
                        <button onClick={() => setShowLinkDialog(false)} className="text-white hover:text-white">
                          <X size={16} />
                        </button>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-white mb-1 block">URL *</label>
                          <input
                            type="url"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            placeholder="https://example.com"
                            className="w-full bg-transparent border border-white/60 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-white"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-white mb-1 block">Link Text (optional)</label>
                          <input
                            type="text"
                            value={linkText}
                            onChange={(e) => setLinkText(e.target.value)}
                            placeholder="Click here"
                            className="w-full bg-transparent border border-white/60 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-white"
                          />
                        </div>
                        <button
                          onClick={handleInsertLink}
                          className="w-full bg-white hover:bg-cyan-600 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Insert Link
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder={`Send ${selectedConversation.type === 'email' ? 'an email' : 'a text'}...`}
                    rows={3}
                    className="flex-1 backdrop-blur-sm bg-transparent border border-white/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white resize-none"
                    style={{ color: 'white' }}
                  />
                  <div className="flex gap-2">
                    {selectedConversation?.type !== 'personal_phone' && (
                      <Button 
                        onClick={() => handleOpenScheduleDialog(false)}
                        className="bg-black hover:bg-white/5 text-white"
                        title="Schedule message"
                      >
                        <Clock size={18} />
                      </Button>
                    )}
                    
                    {/* Show "Send via Mac" and "Send via iPhone" for Personal Phone conversations */}
                    {selectedConversation?.type === 'personal_phone' ? (
                      <>
                        {macMessagesEnabled && (
                          <Button 
                            onClick={handleSendViaMacInConversation}
                            disabled={sendingViaMac || !messageText.trim()}
                            className="bg-gradient-to-r from-purple-600 to-gray-600 hover:from-purple-700 hover:to-gray-700 text-white flex items-center gap-2"
                            title="Send via Mac"
                          >
                            {sendingViaMac ? (
                              <><span className="animate-spin">⏳</span> Sending...</>
                            ) : (
                              <>
                                <Monitor size={18} />
                                <span className="hidden sm:inline">Send via Mac</span>
                              </>
                            )}
                          </Button>
                        )}
                        <Button 
                          onClick={handleSendViaIPhone}
                          className="bg-green-500 hover:bg-green-600 text-white flex items-center gap-2"
                          title="Send via iPhone"
                        >
                          <Phone size={18} />
                          <span className="hidden sm:inline">Send via iPhone</span>
                        </Button>
                      </>
                    ) : selectedConversation?.type === 'text' && macMessagesEnabled ? (
                      <>
                        <Button 
                          onClick={handleSendViaMacInConversation}
                          disabled={sendingViaMac || !messageText.trim()}
                          className="bg-gradient-to-r from-purple-600 to-gray-600 hover:from-purple-700 hover:to-gray-700 text-white flex items-center gap-2"
                          title="Send via Mac"
                        >
                          {sendingViaMac ? (
                            <><span className="animate-spin">⏳</span> Sending...</>
                          ) : (
                            <>
                              <Monitor size={18} />
                              <span className="hidden sm:inline">Send via Mac</span>
                            </>
                          )}
                        </Button>
                        <Button 
                          onClick={handleSendMessage}
                          className="bg-white hover:bg-cyan-600 text-black hover:text-white"
                          title="Send now"
                        >
                          <Send size={18} />
                        </Button>
                      </>
                    ) : (
                      <Button 
                        onClick={handleSendMessage}
                        className="bg-white hover:bg-cyan-600 text-black hover:text-white"
                        title="Send now"
                      >
                        <Send size={18} />
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* AI Draft Response & Schedule Section - Always show for personal_phone */}
                {selectedConversation && (selectedConversation.type === 'personal_phone' || selectedConversation.type === 'text') && (
                  <div className="mt-2">
                    {/* Show draft indicator if draft exists */}
                    {showDraft && selectedConversation.draft_response && (
                      <div className="flex items-center justify-between bg-purple-500/10 border border-purple-500/30 rounded-lg px-3 py-2 mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></div>
                          <span className="text-sm text-purple-400 font-medium">AI-Generated Draft Response</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Draft Actions - Always visible */}
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => handleOpenScheduleDialog(showDraft && selectedConversation.draft_response)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 rounded-lg transition-colors"
                        title="Schedule a message"
                      >
                        <Clock size={14} />
                        {showDraft && selectedConversation.draft_response ? 'Schedule Draft' : 'Schedule Text'}
                      </button>
                      <button
                        onClick={handleRegenerateDraft}
                        disabled={regeneratingDraft}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 rounded-lg transition-colors disabled:opacity-50"
                        title="Generate AI draft response"
                      >
                        <RefreshCw size={14} className={regeneratingDraft ? 'animate-spin' : ''} />
                        {regeneratingDraft ? 'Generating...' : (selectedConversation.draft_response ? 'Regenerate' : 'Generate Draft')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="h-full flex items-center justify-center p-8">
              <div className="text-center">
                <MessageSquare className="text-gray-400 mx-auto mb-6" size={80} />
                <h3 className="text-2xl font-bold text-white mb-3">Select a conversation</h3>
                <p className="text-gray-300 text-lg">Choose a message from the list to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Contact Edit Dialog */}
      <Dialog open={isEditContactOpen} onOpenChange={setIsEditContactOpen}>
        <DialogContent className="bg-black border-white/60 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
            <DialogDescription className="text-white">
              {editingContact ? 'Update contact information' : 'Create a new contact from this conversation'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-white mb-2 block">Name</label>
              <input
                type="text"
                value={contactFormData.name}
                onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
                className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white mb-2 block">Email</label>
              <input
                type="email"
                value={contactFormData.email}
                onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white mb-2 block">Phone</label>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={contactFormData.phone}
                  onChange={(e) => setContactFormData({ ...contactFormData, phone: e.target.value })}
                  className="flex-1 bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!contactFormData.phone) {
                      alert('Please enter a phone number first');
                      return;
                    }
                    
                    console.log('🔍 Auto Scan clicked');
                    console.log('📱 Phone to search:', contactFormData.phone);
                    console.log('📱 iCloud contacts loaded:', icloudContacts.length);
                    console.log('📱 Sample contact:', icloudContacts[0]);
                    
                    const normalized = normalizePhone(contactFormData.phone);
                    console.log('📱 Normalized phone:', normalized);
                    
                    const matchedName = getContactNameByPhone(contactFormData.phone);
                    console.log('📱 Match result:', matchedName);
                    
                    if (matchedName) {
                      setContactFormData({ ...contactFormData, name: matchedName });
                      alert(`✅ Found contact: ${matchedName}\n\nSearched ${icloudContacts.length} iCloud contacts`);
                    } else {
                      alert(`❌ No matching contact found\n\nSearched ${icloudContacts.length} iCloud contacts\nPhone: ${contactFormData.phone}\nNormalized: ${normalized}\n\nTip: Make sure the phone number format matches your iCloud contacts`);
                    }
                  }}
                  className="px-4 py-2 bg-white/30 hover:bg-white/50 text-white rounded-lg transition-colors text-sm whitespace-nowrap"
                  title="Search iCloud contacts for this phone number"
                >
                  Auto Scan
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Enter phone number, then click "Auto Scan" to find match from iCloud contacts</p>
            </div>
            <div>
              <label className="text-sm font-medium text-white mb-2 block">Company</label>
              <input
                type="text"
                value={contactFormData.company}
                onChange={(e) => setContactFormData({ ...contactFormData, company: e.target.value })}
                className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white mb-2 block">Role</label>
              <input
                type="text"
                value={contactFormData.role}
                onChange={(e) => setContactFormData({ ...contactFormData, role: e.target.value })}
                className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white mb-2 block">Category *</label>
              <select
                required
                value={contactFormData.category}
                onChange={(e) => setContactFormData({ ...contactFormData, category: e.target.value })}
                className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
              >
                <option value="">Select category</option>
                <option value="cresa">Cresa</option>
                <option value="software">Software</option>
                <option value="speaking">Speaking</option>
                <option value="investment">Investment</option>
                <option value="personal">Personal</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-white mb-2 block">Notes</label>
              <textarea
                value={contactFormData.notes}
                onChange={(e) => setContactFormData({ ...contactFormData, notes: e.target.value })}
                className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                onClick={() => setIsEditContactOpen(false)}
                variant="outline"
                className="border-white/60 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveContact}
                className="bg-white hover:bg-cyan-600 text-black hover:text-white font-medium"
              >
                {editingContact ? 'Save Changes' : 'Add Contact'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Message Dialog */}
      {/* Edit Scheduled Message Dialog */}
      <Dialog open={isEditScheduleOpen} onOpenChange={setIsEditScheduleOpen}>
        <DialogContent className="bg-black border-white/60 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Scheduled Message</DialogTitle>
            <DialogDescription className="text-white">
              Modify the message text or scheduled time
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-white mb-2 block">Message</label>
              <textarea
                value={editScheduleText}
                onChange={(e) => setEditScheduleText(e.target.value)}
                className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white resize-none"
                rows={4}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white mb-2 block">Schedule Date & Time (CST)</label>
              <input
                type="datetime-local"
                value={editScheduleTime}
                onChange={(e) => setEditScheduleTime(e.target.value)}
                className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                onClick={async () => {
                  if (window.confirm('Cancel this scheduled message?')) {
                    try {
                      await scheduledMessagesAPI.delete(editingScheduledMsg.id);
                      alert('✅ Scheduled message cancelled');
                      setIsEditScheduleOpen(false);
                      loadConversationScheduledMessages(selectedConversation.id);
                    } catch (error) {
                      alert('❌ Failed to cancel: ' + error.message);
                    }
                  }
                }}
                variant="outline"
                className="border-red-700 text-red-400 hover:bg-red-900/20"
              >
                <Trash2 size={16} className="mr-2" />
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await scheduledMessagesAPI.update(editingScheduledMsg.id, {
                      ...editingScheduledMsg,
                      message_text: editScheduleText,
                      scheduled_time: new Date(editScheduleTime).toISOString()
                    });
                    alert('✅ Scheduled message updated');
                    setIsEditScheduleOpen(false);
                    loadConversationScheduledMessages(selectedConversation.id);
                  } catch (error) {
                    alert('❌ Failed to update: ' + error.message);
                  }
                }}
                className="bg-white hover:bg-cyan-600 text-white"
              >
                <Edit size={16} className="mr-2" />
                Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Add to Existing Meeting Selector Dialog */}
      <Dialog open={showMeetingSelector} onOpenChange={setShowMeetingSelector}>
        <DialogContent className="bg-black border-white/60 text-white max-w-2xl max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Add to Existing Meeting</DialogTitle>
            <DialogDescription className="text-gray-300">
              Select a meeting to add this message information
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {existingMeetings.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No upcoming meetings found. Create a new meeting instead.</p>
            ) : (
              <div className="space-y-3">
                {existingMeetings.map((meeting) => (
                  <div
                    key={meeting.id}
                    onClick={async () => {
                      try {
                        // Append message text to meeting notes
                        const updatedNotes = meeting.notes 
                          ? `${meeting.notes}\n\n--- Message from ${selectedConversation.name} ---\n${selectedMessageForMeeting.text}`
                          : `--- Message from ${selectedConversation.name} ---\n${selectedMessageForMeeting.text}`;
                        
                        await apiCall(`/meetings/${meeting.id}`, {
                          method: 'PUT',
                          body: JSON.stringify({
                            notes: updatedNotes
                          })
                        });
                        
                        alert(`✅ Message added to meeting: "${meeting.title}"`);
                        setShowMeetingSelector(false);
                      } catch (error) {
                        console.error('Error adding to meeting:', error);
                        alert('Failed to add message to meeting');
                      }
                    }}
                    className="p-4 bg-transparent hover:bg-transparent border border-white/10 rounded-lg cursor-pointer transition-colors"
                  >
                    <h4 className="text-white font-semibold mb-2">{meeting.title}</h4>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span>📅 {new Date(meeting.date).toLocaleDateString()}</span>
                      <span>🕐 {formatTimeTo12Hour(meeting.time)}</span>
                      <span>⏱️ {meeting.duration}min</span>
                    </div>
                    {meeting.location && (
                      <p className="text-xs text-gray-500 mt-1">📍 {meeting.location}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-white/60">
            <Button
              onClick={() => setShowMeetingSelector(false)}
              className="bg-black hover:bg-white/5 text-white"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent className="bg-black border-white/60 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {isDraftSchedule ? 'Schedule AI Draft Response' : 'Schedule Message'}
            </DialogTitle>
            <DialogDescription className="text-white">
              Schedule this message to be sent at a specific time (Central Standard Time)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-white mb-2 block">Message</label>
              <textarea
                value={messageToSchedule}
                onChange={(e) => setMessageToSchedule(e.target.value)}
                className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white resize-none"
                rows={4}
                placeholder="Enter message to schedule..."
              />
            </div>
            <div>
              <label className="text-sm font-medium text-white mb-2 block">
                Schedule Date & Time (CST)
              </label>
              <input
                type="datetime-local"
                value={scheduleDateTime}
                onChange={(e) => setScheduleDateTime(e.target.value)}
                className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                min={new Date().toISOString().slice(0, 16)}
              />
              <p className="text-xs text-white mt-1">
                Selected: {scheduleDateTime ? new Date(scheduleDateTime).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : 'Not set'}
              </p>
            </div>
            {isDraftSchedule && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                <p className="text-sm text-purple-400">
                  <Clock size={14} className="inline mr-1" />
                  This AI-generated draft will be sent at the scheduled time
                </p>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                onClick={() => setIsScheduleDialogOpen(false)}
                variant="outline"
                className="border-white/60 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleScheduleMessage}
                className="bg-white hover:bg-cyan-600 text-white"
              >
                <Clock size={16} className="mr-2" />
                Schedule Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessageCenter;
